import React, { useState, useEffect, useRef } from 'react';
import { Referral } from '../services/apiService';
import { ApiService } from '../services/apiService';
import {
  isSrReferral,
  canMarkCompleteReferral,
  isSrReferralCancelled,
} from '../services/apiService';
import {
  canCompleteMiscIcpWithoutIndici,
  getMarkCompleteConfirmCopy,
  SR_COMPLETION_OUTBOUND_WARNING,
} from '../utils/referralCompletion';
import { AuthService } from '../services/authService';
import { PDFPreviewPanel, AttachmentLike } from './PDFPreviewPanel';
import { FollowUpComposer } from './FollowUpComposer';
import { SRMessageComposer } from './SRMessageComposer';
import { SRCompleteResponseModal, SRCompleteResponsePayload } from './SRCompleteResponseModal';
import { SRClarificationModal } from './SRClarificationModal';
import { ActionMenu } from './ActionMenu';
import { Button } from './Button';
import { EditableField } from './EditableField';
import { ConfirmationModal } from './ConfirmationModal';
import { BillToCompletedModal } from './BillToCompletedModal';
import { AlertCompletedModal } from './AlertCompletedModal';
import { SendAccEmailModal } from './SendAccEmailModal';
import { TabMenu } from './TabMenu';
import { TabMenuItem } from './TabMenuItem';
import FeatherIcon from 'feather-icons-react';
import { Spinner } from './Spinner';
import { TriageCategoryBadge } from './TriageCategoryBadge';
import { SourceBadge } from './SourceBadge';
import { SimulationBadge } from './SimulationBadge';
import { IngestionWarningsBanner } from './IngestionWarningsBanner';
import { SrChangeBanner } from './sr/SrChangeBanner';
import { SrIndiciCancelButton } from './sr/SrIndiciCancelButton';
import { SrUrgencyBadge } from './sr/SrUrgencyBadge';
import { isAccFunder, getFunderOptions } from '../constants/funders';
import { getReferralBlockers } from '../utils/referralBlockers';

function referralHasFullDetailData(referral: Referral): boolean {
  if (isSrReferral(referral)) {
    // List cache leaves these undefined; detail API always returns arrays (possibly []).
    return !!(referral.attachments || referral.extracted_fields || referral.triage_data);
  }
  return !!(referral.extracted_fields || referral.acc_validation_metadata || referral.attachments);
}

interface ReferralDetailModalProps {
  referral: Referral | null;
  isOpen: boolean;
  onClose: () => void;
  onReferralUpdate?: (updatedReferral: Referral) => void;
  onCompletionWarning?: (message: string) => void;
  userRole?: string;
}

export const ReferralDetailModal: React.FC<ReferralDetailModalProps> = ({
  referral,
  isOpen,
  onClose,
  onReferralUpdate,
  onCompletionWarning,
  userRole: userRoleProp,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'attachments' | 'triage'>('overview');
  const [focusAttachmentId, setFocusAttachmentId] = useState<string | null>(null);
  const [triageNotesExpanded, setTriageNotesExpanded] = useState(false);
  const overviewContentRef = useRef<HTMLDivElement>(null);

  // AXIS162: Triage Notes local editable state (for copy block only - ephemeral, not saved to DB)
  const [editableTriageNotes, setEditableTriageNotes] = useState<{
    key_phrases_matched: string[] | null;
    primary_symptoms: string[] | null;
    functional_limitations: string[] | null;
    provisional_diagnosis: string | null;
    athlete_status: boolean | null;
    post_surgical_status: boolean | null;
  } | null>(null);

  // AXIS162: Copy state for triage notes copy button
  const [triageNotesCopied, setTriageNotesCopied] = useState(false);

  // AXIS166: Copy state for clinical comments copy button
  const [commentsCopied, setCommentsCopied] = useState(false);

  const [downloadingAttachments, setDownloadingAttachments] = useState<Set<string>>(new Set());
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentReferral, setCurrentReferral] = useState<Referral | null>(null);
  // AXISREF439: track exactly which fields the user changed so Save sends a
  // minimal PATCH (dirty fields only) instead of the full referral object.
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  
  // Escalation state
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [escalationNote, setEscalationNote] = useState('');
  const [escalatingReferral, setEscalatingReferral] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [resolvingEscalation, setResolvingEscalation] = useState(false);
  const [escalationError, setEscalationError] = useState<string | null>(null);

  // AXIS214: Reprocess state (standard reprocess - re-runs OCR/AI extraction)
  const [reprocessing, setReprocessing] = useState(false);

  // Copy to clipboard state
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Follow-up Composer state
  const [showFollowUpComposer, setShowFollowUpComposer] = useState(false);
  const [showSRMessageComposer, setShowSRMessageComposer] = useState(false);
  const [showSRClarificationModal, setShowSRClarificationModal] = useState(false);
  const [retryingOutbound, setRetryingOutbound] = useState(false);

  // Awaiting Reply state
  const [showAwaitingReplyModal, setShowAwaitingReplyModal] = useState(false);
  const [awaitingReplyNote, setAwaitingReplyNote] = useState('');
  const [markingAwaitingReply, setMarkingAwaitingReply] = useState(false);
  const [awaitingReplyError, setAwaitingReplyError] = useState<string | null>(null);

  // AXIS210: Approve for Triage state
  const [showApproveForTriageModal, setShowApproveForTriageModal] = useState(false);
  const [approveForTriageNote, setApproveForTriageNote] = useState('');
  const [approvingForTriage, setApprovingForTriage] = useState(false);
  const [approveForTriageError, setApproveForTriageError] = useState<string | null>(null);

  // AXISREF482: explicit approval for declined ACC claims
  const [showDeclinedAccApprovalModal, setShowDeclinedAccApprovalModal] = useState(false);
  const [declinedAccApprovalNote, setDeclinedAccApprovalNote] = useState('');
  const [approvingDeclinedAcc, setApprovingDeclinedAcc] = useState(false);
  const [declinedAccApprovalError, setDeclinedAccApprovalError] = useState<string | null>(null);

  // AXIS076/AXIS235: AE workflow state
  const [showAeNamePrompt, setShowAeNamePrompt] = useState(false);
  const [showBillToCompleted, setShowBillToCompleted] = useState(false);
  const [showAlertCompleted, setShowAlertCompleted] = useState(false);
  const [showSendAccEmail, setShowSendAccEmail] = useState(false);
  const [showSendAccEmailConfirm, setShowSendAccEmailConfirm] = useState(false);
  const [aeWorkflowLoading, setAeWorkflowLoading] = useState(false);
  const [showMarkAeBillingComplete, setShowMarkAeBillingComplete] = useState(false);

  // Validation state
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Close confirmation state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // AXIS212: Upload state for attachments tab
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dismissedIngestionWarnings, setDismissedIngestionWarnings] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AXIS212: Delete state for attachments
  const [deletingAttachments, setDeletingAttachments] = useState<Set<string>>(new Set());
  const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentLike | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // AXIS076: Track whether full referral detail has loaded (vs cached list data)
  const [isFullDataLoaded, setIsFullDataLoaded] = useState(false);

  // AXIS162: Scroll to bottom when triage notes are collapsed
  useEffect(() => {
    if (!triageNotesExpanded && overviewContentRef.current) {
      // Use setTimeout to ensure DOM has updated after state change
      setTimeout(() => {
        if (overviewContentRef.current) {
          overviewContentRef.current.scrollTop = overviewContentRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [triageNotesExpanded]);

  // Only update from props when the modal opens/closes or referral ID changes
  useEffect(() => {
    if (isOpen && referral) {
      // Check if this is a different referral (by ID) or modal just opened
      const isNewReferral = !currentReferral || currentReferral.id !== referral.id;
      
      // AXIS076: Detect whether this is full detail data or cached list data
      // Full data includes fields like extracted_fields, acc_validation_metadata that the list endpoint doesn't return
      const hasFullData = referralHasFullDetailData(referral);
      
      if (isNewReferral) {
        // Modal just opened with a new referral - load the referral data and reset state
        setCurrentReferral(referral);
        setEditingReferral(referral);
        setIsFullDataLoaded(hasFullData);
        setHasChanges(false);
        setDirtyFields(new Set());
        setEditingField(null);
        setShowEscalationModal(false);
        setEscalationNote('');
        setEscalationError(null);
        // AXIS210: Reset approve for triage state
        setShowApproveForTriageModal(false);
        setApproveForTriageNote('');
        setApproveForTriageError(null);
        setShowDeclinedAccApprovalModal(false);
        setDeclinedAccApprovalNote('');
        setDeclinedAccApprovalError(null);
        // AXIS162: Initialize editable triage notes from referral data
        setEditableTriageNotes({
          key_phrases_matched: referral.triage_data?.key_phrases_matched || null,
          primary_symptoms: referral.triage_data?.primary_symptoms || null,
          functional_limitations: referral.triage_data?.functional_limitations || null,
          provisional_diagnosis: referral.triage_data?.provisional_diagnosis || null,
          athlete_status: referral.triage_data?.athlete_status || null,
          post_surgical_status: referral.triage_data?.post_surgical_status || null,
        });
        // AXIS162: Reset triage notes expanded state
        setTriageNotesExpanded(false);
        setDismissedIngestionWarnings(false);
        setFocusAttachmentId(null);
      } else {
        // Same referral, background fetch completed with full data
        setCurrentReferral(referral);
        if (!hasChanges) {
          setEditingReferral(referral);
        }
        if (hasFullData) {
          setIsFullDataLoaded(true);
        }
        // AXIS162: Update editable triage notes from new data (only if not currently being edited)
        // This ensures we get updated values from the server without losing local edits
        setEditableTriageNotes(prev => {
          if (!prev) return {
            key_phrases_matched: referral.triage_data?.key_phrases_matched || null,
            primary_symptoms: referral.triage_data?.primary_symptoms || null,
            functional_limitations: referral.triage_data?.functional_limitations || null,
            provisional_diagnosis: referral.triage_data?.provisional_diagnosis || null,
            athlete_status: referral.triage_data?.athlete_status || null,
            post_surgical_status: referral.triage_data?.post_surgical_status || null,
          };
          return prev;
        });
      }
    } else if (!isOpen) {
      // Modal closed - clear everything
      setCurrentReferral(null);
      setEditingReferral(null);
      setIsFullDataLoaded(false);
      setHasChanges(false);
      setDirtyFields(new Set());
      setEditingField(null);
      setCompleting(false);
      setShowCompleteConfirm(false);
      // AXIS162: Clear triage notes state
      setTriageNotesExpanded(false);
      setEditableTriageNotes(null);
      setTriageNotesCopied(false);
      setCommentsCopied(false);
      // AXIS210: Clear approve for triage state
      setShowApproveForTriageModal(false);
      setApproveForTriageNote('');
      setApproveForTriageError(null);
      setFocusAttachmentId(null);
    }
    // Ignore all other prop updates while modal is open - we own the data
  }, [isOpen, referral?.id, referral]); // React to modal state, referral ID changes, or referral data updates

  if (!isOpen || !currentReferral) {
    return null;
  }

  const isSr = isSrReferral(currentReferral);
  const isCancelled = isSrReferralCancelled(currentReferral);
  const isSrCancelled = isSr && isCancelled;
  const referralBlockers = getReferralBlockers(currentReferral);
  const isBlockedField = (fieldName: string) => referralBlockers.some((blocker) => blocker.field === fieldName);
  const canUseStandardCompletion = canMarkCompleteReferral(currentReferral);
  const canUseMiscCompletion = canCompleteMiscIcpWithoutIndici(currentReferral);
  const showMarkComplete = !isCancelled && currentReferral.status !== 'completed';
  const markCompleteDisabled =
    completing ||
    !isFullDataLoaded ||
    (!canUseStandardCompletion && !canUseMiscCompletion) ||
    referralBlockers.length > 0;
  const completionHelp = !isFullDataLoaded
    ? 'Referral details are still loading.'
    : referralBlockers.length > 0
      ? 'Resolve the blocker summary below before trying Mark Complete.'
      : 'Mark Complete is unavailable for the current referral status.';
  const canApproveDeclinedAcc =
    currentReferral.acc_validation_status_current?.toUpperCase() === 'DECLINED' &&
    !currentReferral.declined_acc_approved_at &&
    currentReferral.status !== 'completed' &&
    !isCancelled;

  const getFieldConfidence = (fieldName: string) => {
    if (!currentReferral?.extracted_fields) return null;
    return currentReferral.extracted_fields.find(f => f.field_name === fieldName);
  };

  const getConfidenceBadgeClass = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return '';
    }
  };

  const getConfidenceClass = (fieldName: string) => {
    const extracted = getFieldConfidence(fieldName);
    // Only show confidence for AI-extracted fields, not manually entered
    if (extracted && extracted.extraction_method !== 'form_input') {
      return getConfidenceBadgeClass(extracted.confidence);
    }
    return '';
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string | null | undefined, fieldName: string) => {
    if (!text) return;
    
    navigator.clipboard.writeText(String(text)).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Handle copy from EditableField
  const handleCopy = (fieldName: string, value: string) => {
    copyToClipboard(value, fieldName);
  };

  // Reusable copy button component
  const CopyButton = ({ value, fieldName }: { value: string | null | undefined, fieldName: string }) => {
    if (!value) return null;

    const isCopied = copiedField === fieldName;

    return (
      <button
        className={`copy-btn ${isCopied ? 'copied' : ''}`}
        onClick={() => copyToClipboard(value, fieldName)}
        title="Copy to clipboard"
      >
        {isCopied ? (
          <FeatherIcon icon="check" size={18} />
        ) : (
          <FeatherIcon icon="copy" size={18} />
        )}
      </button>
    );
  };

  // AXIS162: Helper function to format the copyable triage notes text
  const formatTriageNotesForCopy = (): string => {
    if (!editableTriageNotes || !currentReferral?.triage_data) return '';

    const triageData = currentReferral.triage_data;
    const lines: string[] = [];

    // Add Triage Category and Complexity
    const category = currentReferral.triage_category_final;
    lines.push(`Priority: ${category ? `P${category}` : 'N/A'}`);
    lines.push(`Complexity: ${triageData.complexity ? triageData.complexity.charAt(0).toUpperCase() + triageData.complexity.slice(1) : 'N/A'}`);
    lines.push('');

    // Key Phrases (one per line)
    const keyPhrases = editableTriageNotes.key_phrases_matched;
    if (keyPhrases && keyPhrases.length > 0) {
      lines.push('Key Phrases:');
      keyPhrases.forEach(phrase => lines.push(`- ${phrase}`));
      lines.push('');
    }

    // Primary Symptoms (one per line)
    const symptoms = editableTriageNotes.primary_symptoms;
    if (symptoms && symptoms.length > 0) {
      lines.push('Primary Symptoms:');
      symptoms.forEach(symptom => lines.push(`- ${symptom}`));
      lines.push('');
    }

    // Functional Limitations (one per line)
    const limitations = editableTriageNotes.functional_limitations;
    if (limitations && limitations.length > 0) {
      lines.push('Functional Limitations:');
      limitations.forEach(limitation => lines.push(`- ${limitation}`));
      lines.push('');
    }

    // Provisional Diagnosis
    lines.push(`Provisional Diagnosis: ${editableTriageNotes.provisional_diagnosis || 'N/A'}`);

    // Athlete Status
    const athleteStatus = editableTriageNotes.athlete_status;
    lines.push(`Athlete Status: ${athleteStatus ? 'Yes' : athleteStatus === false ? 'No' : 'N/A'}`);

    // Post Surgical
    const postSurgical = editableTriageNotes.post_surgical_status;
    lines.push(`Post Surgical: ${postSurgical ? 'Yes' : postSurgical === false ? 'No' : 'N/A'}`);

    return lines.join('\n');
  };

  // AXIS162: Helper function to copy triage notes to clipboard
  const copyTriageNotesToClipboard = () => {
    const text = formatTriageNotesForCopy();
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      setTriageNotesCopied(true);
      setTimeout(() => setTriageNotesCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy triage notes:', err);
    });
  };

  // AXIS166: Helper function to copy clinical comments to clipboard
  const copyCommentsToClipboard = () => {
    if (!currentReferral?.referral_comments || currentReferral.referral_comments.length === 0) return;

    const text = currentReferral.referral_comments.map(c => c.body).join('\n\n');

    navigator.clipboard.writeText(text).then(() => {
      setCommentsCopied(true);
      setTimeout(() => setCommentsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy comments:', err);
    });
  };

  // AXIS162: Helper function to update editable triage notes field
  const updateEditableTriageField = (field: keyof NonNullable<typeof editableTriageNotes>, value: any) => {
    setEditableTriageNotes(prev => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  };

  // AXIS162: Helper to parse array field from textarea (one per line)
  const parseArrayField = (value: string): string[] => {
    return value.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  };

  const EMAIL_REQUIRED_FIELDS = new Set([
    'patient_first_name',
    'patient_last_name',
    'patient_dob',
    'nhi_number',
    'contact_phone',
    'contact_email',
    'referral_type',
    'clinical_notes',
    'referring_provider_name',
    'referring_provider_email'
  ]);

  const SR_REQUIRED_FIELDS = new Set([
    'referral_type',
    'is_acc_related',
    'body_side',
    'body_part',
    'referring_provider_name',
    'referring_provider_org',
  ]);

  const getRequiredFields = (referral: Referral | null | undefined): Set<string> => {
    if (!referral || !isSrReferral(referral)) {
      return EMAIL_REQUIRED_FIELDS;
    }
    const fields = new Set(SR_REQUIRED_FIELDS);
    if (isAccFunder(referral.funder)) {
      fields.add('acc_claim_number');
      if (!referral.acc_number_validated) {
        fields.add('acc_number_validated');
      }
    }
    return fields;
  };

  const requiredFields = getRequiredFields(currentReferral);

  const getFieldValueForDisplay = (fieldName: string): string | null => {
    if (fieldName.startsWith('patient_')) {
      return currentReferral?.[fieldName as keyof typeof currentReferral] as string | null;
    } else if (fieldName.startsWith('referral_')) {
      const baseFieldName = fieldName.replace('referral_', '');
      return currentReferral?.[baseFieldName as keyof typeof currentReferral] as string | null;
    } else if (fieldName.startsWith('referring_')) {
      const baseFieldName = fieldName.replace('referring_', '');
      return currentReferral?.[baseFieldName as keyof typeof currentReferral] as string | null;
    }
    return currentReferral?.[fieldName as keyof typeof currentReferral] as string | null;
  };

  // Helper function to get the current value for a field (shows editingReferral if hasChanges, otherwise currentReferral)
  const getFieldValue = (fieldName: keyof Referral): any => {
    // If there are unsaved changes, show the editingReferral value
    if (hasChanges && editingReferral) {
      return editingReferral[fieldName];
    }
    // If this specific field is being edited, show the editingReferral value
    if (editingField === fieldName && editingReferral) {
      return editingReferral[fieldName];
    }
    // Otherwise show the currentReferral value
    return currentReferral?.[fieldName];
  };

  const getConfidenceClassWithMissingCheck = (fieldName: string) => {
    const fieldValue = getFieldValueForDisplay(fieldName);
    
    // If field is required and missing, show red
    if (requiredFields.has(fieldName) && !fieldValue) {
      return 'confidence-low';
    }
    
    // Otherwise use normal confidence logic
    return getConfidenceClass(fieldName);
  };

  const toCapitalCase = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
      .split(/[\s_-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get ACC Status display value (cover status or N/A)
  const getACCStatusDisplay = (referral: Referral): { text: string; className: string } => {
    // If we have a successful validation with cover status, show it
    if (referral.acc_validation_metadata?.coverStatus) {
      return { text: referral.acc_validation_metadata.coverStatus, className: 'validation-status-valid' };
    }
    // If validation ran but claim not found or other error, show N/A
    if (referral.acc_validation_status_current && ['NOT_FOUND', 'INVALID_FORMAT', 'VALIDATION_UNAVAILABLE'].includes(referral.acc_validation_status_current)) {
      return { text: 'N/A', className: 'validation-status-not-provided' };
    }
    // No validation has run
    return { text: 'N/A', className: 'validation-status-not-provided' };
  };

  const formatDateAsDDMMYYYY = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  };

  const formatFileSize = (bytes?: number | null) => {
    if (bytes == null || bytes <= 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadAttachment = async (attachment: any) => {
    if (!currentReferral) return;

    try {
      // Add to downloading set
      setDownloadingAttachments(prev => new Set(prev).add(attachment.id));
      
      console.log('Downloading attachment:', attachment.filename);
      
      if (isSr) {
        await ApiService.downloadSrAttachment(
          currentReferral.id,
          attachment.id,
          attachment.filename,
        );
      } else {
        await ApiService.downloadAttachment(attachment.id, attachment.filename);
      }
    } catch (error) {
      console.error('Download failed:', error);
      // In a real app, you'd show a user-friendly error message
      alert('Failed to download attachment. Please try again.');
    } finally {
      // Remove from downloading set
      setDownloadingAttachments(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Field name mapping for user-friendly display
  const getFieldDisplayLabel = (fieldName: string): string => {
    const fieldLabels: Record<string, string> = {
      'patient_first_name': 'First Name',
      'patient_last_name': 'Last Name',
      'patient_dob': 'Date of Birth',
      'nhi_number': 'NHI Number',
      'contact_method': 'Contact Method (phone or email)',
      'contact_phone': 'Contact Phone',
      'contact_email': 'Contact Email',
      'clinical_notes': 'Clinical Notes',
      'referral_type': 'Referral Type',
      'body_side': 'Body Side',
      'body_part': 'Body Part',
      'acc_claim_number': 'ACC Claim Number',
      'acc_number_validation': 'The ACC Claim Number needs to be successfully validated.',
      'referring_provider_name': 'Provider Name',
      'referring_provider_organization': 'Provider Organisation',
      'referring_provider_contact': 'Provider Contact (email or EDI)',
      'referring_provider_email': 'Provider Email',
      'referring_provider_edi': 'Provider EDI',
    };
    return fieldLabels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleMarkComplete = () => {
    if (!currentReferral || (isSrReferral(currentReferral) && isSrReferralCancelled(currentReferral))) {
      return;
    }

    // AXIS235: Check if ACC Accredited Employer - route through AE workflow
    // Check both funder field and acc_validation_status_current for Accredited Employer
    const isAccreditedEmployer = 
      currentReferral?.funder === 'ACC – Accredited Employer' ||
      currentReferral?.acc_validation_status_current === 'ACCREDITED_EMPLOYER';
    
    if (isAccreditedEmployer) {
      // AXIS235: Show "Do you have the Accredited Employer Name?" prompt
      setShowAeNamePrompt(true);
    } else {
      // Standard completion flow
      setShowCompleteConfirm(true);
      setCompletionError(null);
      setMissingFields([]);
    }
  };

  const confirmMarkComplete = async (srResponse?: SRCompleteResponsePayload) => {
    if (!currentReferral) return;

    setCompleting(true);
    setCompletionError(null);
    setMissingFields([]);
    
    try {
      // AXIS218: Save any pending changes first so the database has the latest funder value
      // This ensures the completeness check uses the current funder, not the stale database value
      if (hasChanges && editingReferral) {
        console.log('Saving pending changes before marking as complete...');

        // AXISREF439: dirty-fields-only PATCH (shared with handleSaveEdit)
        const payload = buildDirtyPatch();
        let savedReferral: Referral | null = null;
        if (Object.keys(payload).length > 0) {
          savedReferral = await ApiService.updateReferral(currentReferral.id, payload);
        }

        // AXIS076: Merge to preserve server-only fields like acc_validation_status_current
        const mergedData = { ...currentReferral, ...(savedReferral ?? {}), ...(editingReferral as any) };
        setCurrentReferral(mergedData);
        setEditingReferral(mergedData);
        setHasChanges(false);
        setDirtyFields(new Set());
        setEditingField(null);
        
        console.log('Pending changes saved successfully');
      }
      
      // Now mark as complete - the database has the latest funder value
      const result = await ApiService.updateReferralStatus(
        currentReferral.id,
        'completed',
        isSr && srResponse
          ? { srResponse }
          : canCompleteMiscIcpWithoutIndici(currentReferral)
            ? { miscNoIndiciConfirm: true }
            : undefined,
      );

      if (result.success === false) {
        throw new Error(
          result.error ||
            result.message ||
            'The referral could not be marked as complete. Please try again.',
        );
      }
      
      // Close the confirmation dialog first
      setShowCompleteConfirm(false);
      
      // Reset completing state immediately since we're closing the modal
      setCompleting(false);

      if (result.sr_message?.attempted && result.sr_message.sent === false) {
        // Completion succeeded locally. Never present the raw SR transport
        // error as though Mark Complete failed; surface a non-blocking warning
        // on the queue when available.
        if (onCompletionWarning) {
          onCompletionWarning(SR_COMPLETION_OUTBOUND_WARNING);
        } else {
          window.alert(SR_COMPLETION_OUTBOUND_WARNING);
        }
      }
      
      // Brief delay to show the confirmation close before closing the modal
      setTimeout(() => {
        // Close the detail modal and trigger parent refresh
        onClose();
      }, 300);
    } catch (err) {
      console.error('Error marking referral as completed:', err);
      
      // Try to extract missing fields from error response
      let errorMessage = 'Failed to mark referral as completed. Please try again.';
      let extractedMissingFields: string[] = [];
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Try to parse error message for missing fields
        // The API should return error with missing_fields array
        try {
          // Check if error message contains JSON-like structure
          const errorMatch = err.message.match(/\{.*\}/);
          if (errorMatch) {
            const errorData = JSON.parse(errorMatch[0]);
            if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
              extractedMissingFields = errorData.missing_fields;
            }
          }
        } catch (parseErr) {
          // If parsing fails, try to extract from the error object itself
          // The ApiService might have already parsed it
        }
      }
      
      // Check if error object has missing_fields property (from API response)
      if (err && typeof err === 'object') {
        const errObj = err as any;
        if (errObj.missing_fields && Array.isArray(errObj.missing_fields)) {
          extractedMissingFields = errObj.missing_fields;
        }
      }
      
      setCompletionError(errorMessage);
      setMissingFields(extractedMissingFields);
      setCompleting(false);
      // Don't close the modal - let user see the error and missing fields
    }
  };

  const cancelMarkComplete = () => {
    setShowCompleteConfirm(false);
    setCompleting(false);
    setCompletionError(null);
    setMissingFields([]);
  };

  // AXIS076: Bill To Completed - Yes path
  const handleBillToCompletedYes = () => {
    setShowBillToCompleted(false);
    setShowAlertCompleted(true);
  };

  // AXIS076: Bill To Completed - No path
  const handleBillToCompletedNo = () => {
    setShowBillToCompleted(false);
    setShowSendAccEmail(true);
  };

  // AXIS076: Alert Completed - Confirm
  const handleAlertCompletedConfirm = async () => {
    if (!currentReferral) return;
    setAeWorkflowLoading(true);
    
    try {
      // Save pending changes first
      if (hasChanges && editingReferral) {
        const payload = buildDirtyPatch();
        if (Object.keys(payload).length > 0) {
          await ApiService.updateReferral(currentReferral.id, payload);
        }
        setHasChanges(false);
        setDirtyFields(new Set());
      }

      // Mark as complete
      await ApiService.updateReferralStatus(currentReferral.id, 'completed');
      setShowAlertCompleted(false);
      setTimeout(() => onClose(), 300);
    } catch (err) {
      console.error('Failed to complete:', err);
    } finally {
      setAeWorkflowLoading(false);
    }
  };

  // AXIS235: Send ACC Email - Show confirmation
  const handleSendAccEmailConfirm = () => {
    setShowSendAccEmail(false);
    setShowSendAccEmailConfirm(true);
  };

  // AXIS235: Send ACC Email - Final confirmation to enter AE queue
  const handleConfirmSendAccEmail = async () => {
    if (!currentReferral) return;
    setAeWorkflowLoading(true);
    
    try {
      // Enter AE queue
      await ApiService.updateAeStatus(currentReferral.id, { enter_ae_queue: true });
      setShowSendAccEmailConfirm(false);
      setTimeout(() => onClose(), 300);
    } catch (err) {
      console.error('Failed to enter AE queue:', err);
    } finally {
      setAeWorkflowLoading(false);
    }
  };

  // AXIS076: Mark ACC Info Received
  const handleMarkAccInfoReceived = async () => {
    if (!currentReferral) return;
    setAeWorkflowLoading(true);
    
    try {
      // Save pending changes first (including acc_employer_name)
      if (hasChanges && editingReferral) {
        const payload = buildDirtyPatch();
        if (Object.keys(payload).length > 0) {
          await ApiService.updateReferral(currentReferral.id, payload);
        }
        setHasChanges(false);
        setDirtyFields(new Set());
      }

      // Mark ACC info as received
      await ApiService.updateAeStatus(currentReferral.id, { acc_info_received: true });
      
      // Trigger Bill To Completed flow
      setShowBillToCompleted(true);
    } catch (err) {
      console.error('Failed to mark ACC info received:', err);
    } finally {
      setAeWorkflowLoading(false);
    }
  };

  // AXIS235: AE Name Prompt - Yes path (has employer name)
  const handleAeNameYes = () => {
    setShowAeNamePrompt(false);
    // Proceed to standard mark complete confirmation
    setShowCompleteConfirm(true);
    setCompletionError(null);
    setMissingFields([]);
  };

  // AXIS235: AE Name Prompt - No path (doesn't have employer name)
  const handleAeNameNo = () => {
    setShowAeNamePrompt(false);
    // Open SendAccEmailModal to send email to ACC
    setShowSendAccEmail(true);
  };

  // AXIS235: Mark AE Billing Complete
  const handleMarkAeBillingComplete = () => {
    setShowMarkAeBillingComplete(true);
  };

  const handleConfirmMarkAeBillingComplete = async () => {
    if (!currentReferral) return;
    setAeWorkflowLoading(true);
    
    try {
      // Mark AE billing as complete
      await ApiService.updateAeStatus(currentReferral.id, { ae_billing_completed: true });
      
      // Mark referral as completed
      await ApiService.updateReferralStatus(currentReferral.id, 'completed');
      
      setShowMarkAeBillingComplete(false);
      setTimeout(() => onClose(), 300);
    } catch (err) {
      console.error('Failed to mark AE billing complete:', err);
    } finally {
      setAeWorkflowLoading(false);
    }
  };

  const handleStartEditing = (fieldName: string) => {
    setEditingField(fieldName);
  };

  const handleFieldBlur = () => {
    setEditingField(null);
  };

  const checkIfComplete = (referral: any): boolean => {
    if (isSrReferral(referral)) {
      if (referral.is_complete != null) {
        return referral.is_complete === true;
      }
      for (const field of getRequiredFields(referral)) {
        const value = referral[field];
        if (field === 'is_acc_related') {
          if (value === null || value === undefined) return false;
          continue;
        }
        if (field === 'acc_number_validated') {
          if (!value) return false;
          continue;
        }
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return false;
        }
      }
      return true;
    }

    for (const field of EMAIL_REQUIRED_FIELDS) {
      const value = referral[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return false;
      }
    }
    return true;
  };

  const handleSaveEdit = async () => {
    if (!editingReferral || !currentReferral) {
      console.log('No editingReferral, returning early');
      return;
    }

    try {
      setSaving(true);

      // AXISREF439: send only the fields the user actually changed (dirty
      // fields). Sending the full object would falsely claim ownership of
      // every displayed value and resend unchanged server state.
      const payload = buildDirtyPatch();

      let updatedReferral: Referral;
      if (Object.keys(payload).length > 0) {
        updatedReferral = await ApiService.updateReferral(currentReferral.id, payload);
      } else {
        updatedReferral = currentReferral;
      }

      // AXISREF439: persist editable triage notes (previously local-only —
      // edits were lost on close). Send only fields that actually differ
      // from the saved triage_data through the dedicated triage endpoint,
      // which supports both standard and SR referrals.
      if (canEditTriageNotesFields && editableTriageNotes && currentReferral.triage_data) {
        const saved = currentReferral.triage_data;
        const arraysEqual = (a: string[] | null, b: string[] | null | undefined) =>
          JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
        const triageUpdates: Record<string, any> = {};
        if (!arraysEqual(editableTriageNotes.key_phrases_matched, saved.key_phrases_matched)) {
          triageUpdates.key_phrases_matched = editableTriageNotes.key_phrases_matched ?? [];
        }
        if (!arraysEqual(editableTriageNotes.primary_symptoms, saved.primary_symptoms)) {
          triageUpdates.primary_symptoms = editableTriageNotes.primary_symptoms ?? [];
        }
        if (!arraysEqual(editableTriageNotes.functional_limitations, saved.functional_limitations)) {
          triageUpdates.functional_limitations = editableTriageNotes.functional_limitations ?? [];
        }
        if ((editableTriageNotes.provisional_diagnosis ?? null) !== (saved.provisional_diagnosis ?? null)) {
          triageUpdates.provisional_diagnosis = editableTriageNotes.provisional_diagnosis ?? null;
        }
        if (Object.keys(triageUpdates).length > 0) {
          try {
            const triageResult = await ApiService.updateTriageCategory(
              currentReferral.id,
              undefined,
              triageUpdates as any,
            );
            updatedReferral = { ...updatedReferral, ...triageResult };
          } catch (triageErr) {
            console.error('Failed to save triage notes:', triageErr);
            alert('Referral fields saved, but triage notes failed to save. Please retry.');
          }
        }
      }

      
      // Email path: client-side promotion fallback; SR path relies on backend completeness sync
      if (!isSr) {
        const isComplete = checkIfComplete(updatedReferral);
        
        if (isComplete && updatedReferral.status !== 'ready_for_indici' && updatedReferral.status !== 'completed') {
          try {
            await ApiService.updateReferralStatus(currentReferral.id, 'ready_for_indici');
            updatedReferral = { ...updatedReferral, status: 'ready_for_indici' };
          } catch (statusErr) {
            console.error('Failed to update status to ready_for_indici:', statusErr);
          }
        }
      }
      
      // AXIS076: Merge server response with current data to preserve server-only fields
      // (like acc_validation_status_current, acc_validation_metadata) that the
      // update endpoint may not return but are critical for workflow routing
      const mergedReferral = { ...currentReferral, ...updatedReferral };
      setCurrentReferral(mergedReferral);
      setEditingReferral(mergedReferral);

      // AXISREF439: re-sync the triage note editors to the saved values so a
      // second Save does not resend identical triage updates.
      if (mergedReferral.triage_data) {
        setEditableTriageNotes(prev => prev ? {
          ...prev,
          key_phrases_matched: mergedReferral.triage_data!.key_phrases_matched ?? prev.key_phrases_matched,
          primary_symptoms: mergedReferral.triage_data!.primary_symptoms ?? prev.primary_symptoms,
          functional_limitations: mergedReferral.triage_data!.functional_limitations ?? prev.functional_limitations,
          provisional_diagnosis: mergedReferral.triage_data!.provisional_diagnosis ?? prev.provisional_diagnosis,
        } : prev);
      }

      // Update the referral in the parent component
      if (onReferralUpdate) {
        onReferralUpdate(mergedReferral);
      } else {
        console.log('No onReferralUpdate callback provided');
      }
      
      
      setHasChanges(false);
      setDirtyFields(new Set());
      setEditingField(null);
    } catch (err) {
      console.error('Error updating referral:', err);
      const message = err instanceof Error ? err.message : 'Failed to update referral. Please try again.';
      alert(`Failed to update referral: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof Referral, value: string) => {
    if (editingReferral) {
      let processedValue: any = value;
      
      // Handle special field types
      if (field === 'is_acc_related' || field === 'acc_number_validated') {
        if (value === '') {
          processedValue = null;
        } else {
          processedValue = value === 'true';
        }
      }
      
      // Filter contact phone to only allow valid phone number characters
      if (field === 'contact_phone') {
        // Allow: numbers, +, spaces, hyphens, parentheses, periods, forward slashes
        processedValue = value.replace(/[^0-9+\s\-()./]/g, '');
      }
      
      setEditingReferral({
        ...editingReferral,
        [field]: processedValue
      });
      setDirtyFields(prev => new Set(prev).add(field));
      setHasChanges(true);
    }
  };

  // AXIS218: Handle funder field changes with special logic for ACC fields
  const handleFunderChange = (value: string) => {
    if (editingReferral) {
      const newFunder = value;
      const isNewFunderAcc = isAccFunder(newFunder);

      // If changing to non-ACC funder, clear the ACC claim number to avoid stale data
      const updates: Partial<Referral> = {
        funder: newFunder,
      };

      const touched: Array<keyof Referral> = ['funder'];

      if (!isNewFunderAcc) {
        // Clear ACC claim number when switching to non-ACC funder
        updates.acc_claim_number = null;
        // Also update is_acc_related to false for non-ACC funders
        updates.is_acc_related = false;
      } else {
        // Set is_acc_related to true for ACC funders
        updates.is_acc_related = true;
      }
      touched.push('is_acc_related');
      if ('acc_claim_number' in updates) {
        touched.push('acc_claim_number');
      }

      setEditingReferral({
        ...editingReferral,
        ...updates
      } as Referral);
      setDirtyFields(prev => {
        const next = new Set(prev);
        touched.forEach(f => next.add(f));
        return next;
      });
      setHasChanges(true);
    }
  };

  // AXISREF439: Build the minimal PATCH payload of dirty fields only. Date
  // fields are normalized to date-only strings so the backend accepts them.
  const buildDirtyPatch = (): Partial<Referral> => {
    if (!editingReferral) return {};
    const patch: Record<string, any> = {};
    dirtyFields.forEach(field => {
      if (!(field in editingReferral)) return;
      let value: any = (editingReferral as any)[field];
      if (value && typeof value === 'object' && value.constructor === Date) {
        value = field === 'patient_dob'
          ? value.toISOString().split('T')[0]
          : value.toISOString();
      }
      if (typeof value === 'string' && value.includes('T')) {
        if (field === 'patient_dob') {
          value = value.split('T')[0];
        }
      }
      patch[field] = value;
    });
    return patch;
  };

  // Escalation handlers
  const handleEscalate = async () => {
    if (!currentReferral) return;
    
    setEscalatingReferral(true);
    setEscalationError(null);
    
    try {
      // Get auth token from MSAL
      const token = await AuthService.getAccessToken();
      if (!token) {
        setEscalationError('Authentication required. Please log in again.');
        setEscalatingReferral(false);
        return;
      }
      
      // Get current user info to auto-include in escalation note
      const firstName = localStorage.getItem('user_first_name') || '';
      const lastName = localStorage.getItem('user_last_name') || '';
      const userName = `${firstName} ${lastName}`.trim() || 'Staff Member';
      
      // Prepend escalator info to the note
      const notePrefix = `[Escalated by: ${userName}]\n`;
      const finalNote = notePrefix + escalationNote;
      
      // Call escalate API
      const updated = await ApiService.escalateReferral(
        currentReferral.id,
        finalNote,
        token
      );
      
      // Update local state
      setCurrentReferral(updated);
      setShowEscalationModal(false);
      setEscalationNote('');
      
      // Notify parent component
      onReferralUpdate?.(updated);
      
      // Close the detail modal to take user back to their previous screen
      onClose();
      
    } catch (err) {
      console.error('Error escalating referral:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to escalate referral. Please try again.';
      setEscalationError(errorMsg);
    } finally {
      setEscalatingReferral(false);
    }
  };

  // AXIS210: Handle approve for triage (promote Miscellaneous to AI triage)
  const handleApproveForTriage = async () => {
    if (!currentReferral) return;

    setApprovingForTriage(true);
    setApproveForTriageError(null);

    try {
      // Call approve for triage API
      const result = await ApiService.approveForTriage(
        currentReferral.id,
        approveForTriageNote || undefined
      );

      // Update local state
      setCurrentReferral(result.referral);
      setShowApproveForTriageModal(false);
      setApproveForTriageNote('');

      // Notify parent component
      onReferralUpdate?.(result.referral);

      // Close the detail modal - the referral will no longer be in Miscellaneous
      onClose();

    } catch (err) {
      console.error('Error approving for triage:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to approve for triage. Please try again.';
      setApproveForTriageError(errorMsg);
    } finally {
      setApprovingForTriage(false);
    }
  };

  // AXISREF482: record the explicit staff exception, then refresh the detail payload.
  const handleApproveDeclinedAcc = async () => {
    if (!currentReferral) return;
    const note = declinedAccApprovalNote.trim();
    if (!note) {
      setDeclinedAccApprovalError('A note is required to approve a declined ACC claim.');
      return;
    }

    setApprovingDeclinedAcc(true);
    setDeclinedAccApprovalError(null);
    try {
      await ApiService.approveDeclinedAcc(currentReferral.id, note);
      const updated = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(updated);
      setEditingReferral(updated);
      setShowDeclinedAccApprovalModal(false);
      setDeclinedAccApprovalNote('');
      onReferralUpdate?.(updated);
    } catch (err) {
      console.error('Error approving declined ACC claim:', err);
      setDeclinedAccApprovalError(
        err instanceof Error ? err.message : 'Failed to approve the declined ACC claim. Please try again.',
      );
    } finally {
      setApprovingDeclinedAcc(false);
    }
  };

  const handleResolveEscalation = async () => {
    if (!currentReferral) return;
    
    setResolvingEscalation(true);
    setEscalationError(null);
    
    try {
      const token = await AuthService.getAccessToken();
      if (!token) {
        setEscalationError('Authentication required. Please log in again.');
        setResolvingEscalation(false);
        return;
      }
      
      const updated = await ApiService.resolveEscalation(
        currentReferral.id,
        token
      );
      
      setCurrentReferral(updated);
      setShowResolveConfirm(false);
      onReferralUpdate?.(updated);
      
      // Close the detail modal to take user back to escalations screen
      onClose();
      
    } catch (err) {
      console.error('Error resolving escalation:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to resolve escalation. Please try again.';
      setEscalationError(errorMsg);
    } finally {
      setResolvingEscalation(false);
    }
  };

  const handleMarkAwaitingReply = async () => {
    if (!currentReferral) return;
    
    setMarkingAwaitingReply(true);
    setAwaitingReplyError(null);
    
    try {
      const token = await AuthService.getAccessToken();
      if (!token) {
        setAwaitingReplyError('Authentication required. Please log in again.');
        setMarkingAwaitingReply(false);
        return;
      }
      
      const updated = await ApiService.markAwaitingReply(
        currentReferral.id,
        awaitingReplyNote,
        token
      );
      
      setCurrentReferral(updated);
      setShowAwaitingReplyModal(false);
      setAwaitingReplyNote('');
      
      // Notify parent component
      onReferralUpdate?.(updated);
      
      // Close the detail modal to take user back to their previous screen
      onClose();
      
    } catch (err) {
      console.error('Error marking referral as awaiting reply:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to mark referral as awaiting reply. Please try again.';
      setAwaitingReplyError(errorMsg);
    } finally {
      setMarkingAwaitingReply(false);
    }
  };

  const handleRetryOutbound = async () => {
    if (!currentReferral || (isSrReferral(currentReferral) && isSrReferralCancelled(currentReferral))) return;

    setRetryingOutbound(true);
    try {
      await ApiService.retrySROutbound(currentReferral.id);
      const updatedReferral = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(updatedReferral);
      setEditingReferral(updatedReferral);
      onReferralUpdate?.(updatedReferral);
    } catch (err) {
      console.error('Error retrying SR outbound:', err);
    } finally {
      setRetryingOutbound(false);
    }
  };

  // AXIS214: Standard Reprocess action - re-runs OCR/AI field extraction and duplicate detection
  // Does NOT affect triage_approved status (unlike Reprocess Triage)
  const handleReprocess = async () => {
    if (!currentReferral || (isSrReferral(currentReferral) && isSrReferralCancelled(currentReferral))) return;

    setReprocessing(true);

    try {
      const result = await ApiService.reprocessReferral(currentReferral.id);

      if (result.success) {
        // Refresh the referral data to show updated information
        try {
          const updatedReferral = await ApiService.getReferralById(currentReferral.id);
          setCurrentReferral(updatedReferral);
          setEditingReferral(updatedReferral);
          onReferralUpdate?.(updatedReferral);
        } catch (refreshErr) {
          console.error('Error refreshing referral after reprocess:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Error reprocessing referral:', err);
    } finally {
      setReprocessing(false);
    }
  };

  const handleValidateACC = async () => {
    if (!currentReferral) return;
    
    setValidating(true);
    setValidationError(null);
    
    try {
      // Trigger validation with force=true to bypass cache
      await ApiService.triggerValidation(currentReferral.id, true);
      
      // Refresh the referral data to show updated validation status
      try {
        const updatedReferral = await ApiService.getReferralById(currentReferral.id);
        setCurrentReferral(updatedReferral);
        setEditingReferral(updatedReferral);
        onReferralUpdate?.(updatedReferral);
        // Clear any previous errors on success
        setValidationError(null);
      } catch (refreshErr) {
        console.error('Error refreshing referral after validation:', refreshErr);
        setValidationError('Validation completed but failed to refresh status. Please reload the page.');
      }
    } catch (err) {
      console.error('Error validating ACC number:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to validate ACC number. Please try again.';
      setValidationError(errorMsg);
    } finally {
      setValidating(false);
    }
  };

  // AXIS212: Upload attachment handlers
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentReferral) return;

    // Reset state
    setUploadError(null);
    setUploadProgress(0);

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    const allowedExts = ['.pdf', '.docx', '.doc', '.jpeg', '.jpg', '.png'];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
      const allowed = allowedExts.join(', ');
      setUploadError(`Invalid file type. Allowed types: ${allowed}`);
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File too large. Maximum size: ${maxSize / 1024 / 1024}MB`);
      return;
    }

    // Start upload
    setUploadingAttachment(true);
    try {
      await ApiService.uploadAttachment(
        currentReferral.id,
        file,
        (progress) => setUploadProgress(progress)
      );

      // Refresh referral data to get updated attachments
      const updatedReferral = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(updatedReferral);
      setEditingReferral(updatedReferral);
      onReferralUpdate?.(updatedReferral);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingAttachment(false);
      setUploadProgress(0);
    }
  };

  // AXIS212: Handle attachment uploaded from PDF preview panel
  const handleAttachmentUploaded = (_newAttachment: AttachmentLike) => {
    if (currentReferral) {
      // Refresh referral data to get updated attachments
      ApiService.getReferralById(currentReferral.id)
        .then(updatedReferral => {
          setCurrentReferral(updatedReferral);
          setEditingReferral(updatedReferral);
          onReferralUpdate?.(updatedReferral);
        })
        .catch(err => {
          console.error('Failed to refresh referral after upload:', err);
        });
    }
  };

  // AXIS212: Delete attachment handlers
  const handleDeleteClick = (attachment: AttachmentLike) => {
    setAttachmentToDelete(attachment);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!attachmentToDelete || !currentReferral) return;

    setDeletingAttachments(prev => new Set(prev).add(attachmentToDelete.id));
    setShowDeleteConfirm(false);

    try {
      await ApiService.deleteAttachment(currentReferral.id, attachmentToDelete.id);

      // Update local state to remove the deleted attachment
      const updatedAttachments = currentReferral.attachments?.filter(
        att => att.id !== attachmentToDelete.id
      ) || [];

      const updatedReferral = {
        ...currentReferral,
        attachments: updatedAttachments,
        attachment_count: updatedAttachments.length
      };

      setCurrentReferral(updatedReferral);
      setEditingReferral(updatedReferral);
      onReferralUpdate?.(updatedReferral);

      console.log(`[AXIS212] Attachment deleted: ${attachmentToDelete.filename}`);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to delete attachment');
    } finally {
      setDeletingAttachments(prev => {
        const next = new Set(prev);
        next.delete(attachmentToDelete.id);
        return next;
      });
      setAttachmentToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setAttachmentToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Get user role from localStorage (for permission checks)
  // Get user role from prop or fallback to empty string
  const userRole = userRoleProp?.toLowerCase() || '';
  const canEscalate = !currentReferral?.is_escalated;
  const canResolve = userRole === 'admin' && currentReferral?.is_escalated;
  const canRetryOutbound = isSr && (
    currentReferral.sr_outbound_pending ||
    (userRole === 'admin' && (!!currentReferral.extraction_failed_at || currentReferral.pdf_extraction_pending))
  );
  const canMarkAwaitingReply = true;

  // AXIS210: Check if user can approve for triage (promote Miscellaneous to AI triage)
  // Any role can perform this action (admin, manager, clinician, reception, pa)
  const canApproveForTriage = currentReferral?.is_miscellaneous === true;

  // AXIS162: Check if user can edit triage notes fields (local edit before copy)
  const canEditTriageNotesFields = userRole !== 'clinician' && !currentReferral?.triage_approved;

  // Add this new function to check if user can generate follow-ups
  const canGenerateFollowUp = () => {
    // Allow all staff to generate follow-ups at any time for any referral
    return currentReferral !== null;
  };

  // Handle close request - check for unsaved changes
  const handleCloseRequest = () => {
    if (hasChanges) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  // Confirm close and discard changes
  const confirmClose = () => {
    setShowCloseConfirm(false);
    setHasChanges(false);
    onClose();
  };

  // Cancel close and keep modal open
  const cancelClose = () => {
    setShowCloseConfirm(false);
  };

  // Confirm resolve escalation
  const confirmResolveEscalation = async () => {
    await handleResolveEscalation();
  };

  // Cancel resolve escalation
  const cancelResolveEscalation = () => {
    setShowResolveConfirm(false);
  };

  return (
    <>
      {/* Detail Modal */}
      <div className="modal-overlay" onClick={handleCloseRequest}>
        <div 
          className="modal-content" 
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: 'calc(100vh - 10rem)', display: 'flex', flexDirection: 'column' }}
        >
          <div className="modal-header">
            <h2>
              {currentReferral.patient_first_name} {currentReferral.patient_last_name}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: '10px',
                  verticalAlign: 'middle',
                }}
              >
                <SourceBadge source={currentReferral.source} />
                {currentReferral.is_simulation && <SimulationBadge />}
                {isSr && <SrUrgencyBadge isUrgent={currentReferral.is_urgent} />}
              </span>
              {isSr && currentReferral.sr_referral_number && (
                <span
                  style={{
                    marginLeft: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    verticalAlign: 'middle',
                  }}
                >
                  {currentReferral.sr_referral_number}
                </span>
              )}
            </h2>
            <div className="modal-actions">
              {isSr && (
                <SrIndiciCancelButton
                  referral={currentReferral}
                  userRole={userRole}
                  onAcknowledged={async () => {
                    try {
                      const updated = await ApiService.getReferralById(currentReferral.id);
                      setCurrentReferral(updated);
                      onReferralUpdate?.(updated);
                    } catch (err) {
                      console.error('Failed to refresh referral after Indici ack', err);
                    }
                  }}
                />
              )}
              <Button 
                variant="success" 
                onClick={handleSaveEdit}
                disabled={saving || !hasChanges}
                isLoading={saving}
                size="sm"
              >
                Save Changes
              </Button>
              {showMarkComplete && (
                <>
                <Button
                  variant="success"
                  onClick={handleMarkComplete}
                  disabled={markCompleteDisabled}
                  isLoading={completing || !isFullDataLoaded}
                  size="sm"
                  aria-describedby={markCompleteDisabled ? 'referral-completion-help' : undefined}
                  title={!isFullDataLoaded
                    ? 'Loading referral data...'
                    : referralBlockers.length > 0
                      ? referralBlockers.map((blocker) => `${blocker.label}: ${blocker.action}`).join(' ')
                      : undefined}
                >
                  {!isFullDataLoaded ? 'Loading...' : 'Mark Complete'}
                </Button>
                {markCompleteDisabled && (
                  <span id="referral-completion-help" className="referral-completion-help">
                    {completionHelp}
                  </span>
                )}
                </>
              )}
              {canResolve && (
                <Button 
                  variant="success"
                  onClick={() => setShowResolveConfirm(true)}
                  disabled={resolvingEscalation}
                  isLoading={resolvingEscalation}
                  size="sm"
                >
                  Resolve Escalation
                </Button>
              )}
              <ActionMenu
                icon="more-horizontal"
                iconSize={20}
                options={[
                  ...(isSr ? [{
                    label: reprocessing ? 'Re-extracting PDF...' : 'Re-extract PDF',
                    onClick: handleReprocess,
                    disabled: reprocessing || isSrCancelled,
                    isLoading: reprocessing,
                  }] : [{
                    label: 'Reprocess',
                    onClick: handleReprocess,
                    disabled: reprocessing,
                    isLoading: reprocessing,
                  }]),
                  ...(currentReferral?.acc_claim_number ? [{
                    label: validating ? 'Validating ACC...' : 'Test ACC Validation',
                    onClick: handleValidateACC,
                    disabled: validating,
                    isLoading: validating,
                  }] : []),
                  ...(isSr ? [{
                    label: 'Message GP',
                    onClick: () => setShowSRMessageComposer(true),
                    disabled: isSrCancelled,
                  }] : (canGenerateFollowUp() ? [{
                    label: 'Generate Follow-up',
                    onClick: () => setShowFollowUpComposer(true),
                    disabled: false
                  }] : [])),
                  ...(isSr &&
                  currentReferral.status !== 'completed' &&
                  currentReferral.status !== 'awaiting_reply'
                    ? [{
                        label: 'Request Clarification',
                        onClick: () => setShowSRClarificationModal(true),
                        disabled: isSrCancelled,
                      }]
                    : []),
                  ...(canEscalate ? [{
                    label: 'Escalate to Admin',
                    onClick: () => setShowEscalationModal(true),
                    disabled: escalatingReferral,
                    isLoading: escalatingReferral,
                  }] : []),
                  ...(!isSr && canApproveForTriage ? [{
                    label: 'Approve for Triage',
                    onClick: () => setShowApproveForTriageModal(true),
                    disabled: approvingForTriage,
                    isLoading: approvingForTriage,
                  }] : []),
                  ...(canMarkAwaitingReply && !isSr ? [{
                    label: 'Mark as Awaiting Reply',
                    onClick: () => setShowAwaitingReplyModal(true),
                    disabled: markingAwaitingReply,
                    isLoading: markingAwaitingReply,
                  }] : []),
                  ...(canRetryOutbound ? [{
                    label: retryingOutbound ? 'Retrying outbound...' : 'Retry outbound',
                    onClick: handleRetryOutbound,
                    disabled: retryingOutbound || isSrCancelled,
                    isLoading: retryingOutbound,
                  }] : []),
                ]}
              />
              <Button
                variant="icon"
                onClick={handleCloseRequest}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
          </div>

          <div className="modal-body">

            {/* AXIS076: Loading banner when full data hasn't arrived yet */}
            {!isFullDataLoaded && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#856404',
              }}>
                <Spinner size="sm" color="primary" />
                <span>Loading full referral data...</span>
              </div>
            )}

            {referralBlockers.length > 0 && (
              <section
                className="referral-blocker-banner"
                role="alert"
                aria-labelledby="referral-blocker-heading"
              >
                <h4 id="referral-blocker-heading">Can't complete this referral yet</h4>
                <p>Resolve the following blocker{referralBlockers.length === 1 ? '' : 's'} before trying Mark Complete:</p>
                <ul>
                  {referralBlockers.map((blocker) => (
                    <li key={blocker.code} className={`referral-blocker-${blocker.tone}`}>
                      <strong>{blocker.label}:</strong> {blocker.problem} {blocker.action}
                    </li>
                  ))}
                </ul>
                {canApproveDeclinedAcc && (
                  <div className="referral-blocker-action">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => {
                        setShowDeclinedAccApprovalModal(true);
                        setDeclinedAccApprovalError(null);
                      }}
                    >
                      Approve declined ACC claim
                    </Button>
                    <span>Approval requires a staff note and is recorded for audit.</span>
                  </div>
                )}
              </section>
            )}

            {currentReferral.declined_acc_approved_at && (
              <section className="referral-acc-approval" role="status" aria-label="Declined ACC approval">
                <strong>Declined ACC claim approved</strong>
                <span>
                  {new Date(currentReferral.declined_acc_approved_at).toLocaleString()}
                  {currentReferral.declined_acc_approved_by_user_id
                    ? ` by ${currentReferral.declined_acc_approved_by_user_id}`
                    : ''}
                </span>
                {currentReferral.declined_acc_approval_note && (
                  <span>Staff note: {currentReferral.declined_acc_approval_note}</span>
                )}
              </section>
            )}

            {isSr && (
              <SrChangeBanner
                referral={currentReferral}
                onViewAddendum={(attachmentId) => {
                  setFocusAttachmentId(attachmentId);
                  setActiveTab('overview');
                }}
              />
            )}

            <TabMenu variant="queue" >
              <TabMenuItem
                id="overview"
                label="Overview"
                isActive={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              />
              <TabMenuItem
                id="attachments"
                label="Attachments"
                isActive={activeTab === 'attachments'}
                onClick={() => setActiveTab('attachments')}
              />
            </TabMenu>

            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  <div className="overview-content" ref={overviewContentRef}>
                    <IngestionWarningsBanner
                      warnings={currentReferral.ingestion_warnings || []}
                      dismissed={dismissedIngestionWarnings}
                      onDismiss={() => setDismissedIngestionWarnings(true)}
                    />
                    {isSr && currentReferral.extraction_failed_at && (
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ef9a9a',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#c62828',
                      }}>
                        PDF extraction failed. Use &quot;Re-extract PDF&quot; to retry.
                      </div>
                    )}
                    {isSr && currentReferral.triage_failed_at && (
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffcc80',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#e65100',
                      }}>
                        Triage extraction failed. Re-extract the PDF or contact support if the issue persists.
                      </div>
                    )}
                    {/* Standard Mode - Show Patient/Referral/Clinical Notes */}
                    <>
                        {/* Escalation Information Section - Show when escalated */}
                        {!triageNotesExpanded && currentReferral.is_escalated && (
                          <div className="escalation-info-section">
                            <h4>🚨 Escalation Information</h4>
                            <div className="escalation-details">
                              <div className="detail-item">
                                <label>Status:</label>
                                <span>Escalated to Admin</span>
                              </div>
                              {currentReferral.escalated_at && (
                                <div className="detail-item">
                                  <label>Escalated At:</label>
                                  <span>{formatDateAsDDMMYYYY(currentReferral.escalated_at)}</span>
                                </div>
                              )}
                              {currentReferral.escalation_note && (
                                <div className="detail-item full-width">
                                  <label>Escalation Notes:</label>
                                  <p className="escalation-note-box">{currentReferral.escalation_note}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Awaiting Reply Information Section - AXIS231 - Show when awaiting reply */}
                        {!triageNotesExpanded && currentReferral.status === 'awaiting_reply' && currentReferral.awaiting_reply_at && (
                          <div className="awaiting-reply-info-section">
                            <h4>⏳ Awaiting Reply Information</h4>
                            <div className="awaiting-reply-details">
                              <div className="detail-item">
                                <label>Status:</label>
                                <span>Awaiting Reply</span>
                              </div>
                              {currentReferral.awaiting_reply_at && (
                                <div className="detail-item">
                                  <label>Marked At:</label>
                                  <span>{formatDateAsDDMMYYYY(currentReferral.awaiting_reply_at)}</span>
                                </div>
                              )}
                              {currentReferral.awaiting_reply_note && (
                                <div className="detail-item full-width">
                                  <label>Awaiting Reply Notes:</label>
                                  <p className="awaiting-reply-note-box">{currentReferral.awaiting_reply_note}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Patient Information Section */}
                    {!triageNotesExpanded && (
                    <div className="patient-details">
                      <h4>Patient Information</h4>
                      <div className="detail-grid">
                        <EditableField
                          label="NHI Number"
                          value={getFieldValue('nhi_number')}
                          fieldName="nhi_number"
                          className={isBlockedField('nhi_number') ? 'blocked-field' : ''}
                          isEditing={editingField === 'nhi_number'}
                          onStartEdit={() => handleStartEditing('nhi_number')}
                          onChange={(value) => handleFieldChange('nhi_number', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('nhi_number')}
                          showCopyButton
                          onCopy={handleCopy}
                          maxLength={7}
                          placeholder="7 characters"
                        />
                        <EditableField
                          label="First Name"
                          value={getFieldValue('patient_first_name')}
                          fieldName="patient_first_name"
                          className={isBlockedField('patient_first_name') ? 'blocked-field' : ''}
                          isEditing={editingField === 'patient_first_name'}
                          onStartEdit={() => handleStartEditing('patient_first_name')}
                          onChange={(value) => handleFieldChange('patient_first_name', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('patient_first_name')}
                          formatValue={toCapitalCase}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                        <EditableField
                          label="Last Name"
                          value={getFieldValue('patient_last_name')}
                          fieldName="patient_last_name"
                          className={isBlockedField('patient_last_name') ? 'blocked-field' : ''}
                          isEditing={editingField === 'patient_last_name'}
                          onStartEdit={() => handleStartEditing('patient_last_name')}
                          onChange={(value) => handleFieldChange('patient_last_name', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('patient_last_name')}
                          formatValue={toCapitalCase}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                        <EditableField
                          label="Date of Birth"
                          value={getFieldValue('patient_dob')}
                          fieldName="patient_dob"
                          className={isBlockedField('patient_dob') ? 'blocked-field' : ''}
                          isEditing={editingField === 'patient_dob'}
                          onStartEdit={() => handleStartEditing('patient_dob')}
                          onChange={(value) => handleFieldChange('patient_dob', value)}
                          onBlur={handleFieldBlur}
                          type="date"
                          confidenceClass={getConfidenceClassWithMissingCheck('patient_dob')}
                          formatValue={formatDateAsDDMMYYYY}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                        <EditableField
                          label="Contact Number"
                          value={getFieldValue('contact_phone')}
                          fieldName="contact_phone"
                          className={isBlockedField('contact_phone') ? 'blocked-field' : ''}
                          isEditing={editingField === 'contact_phone'}
                          onStartEdit={() => handleStartEditing('contact_phone')}
                          onChange={(value) => handleFieldChange('contact_phone', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('contact_phone')}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                        <EditableField
                          label="Contact Email"
                          value={getFieldValue('contact_email')}
                          fieldName="contact_email"
                          className={isBlockedField('contact_email') ? 'blocked-field' : ''}
                          isEditing={editingField === 'contact_email'}
                          onStartEdit={() => handleStartEditing('contact_email')}
                          onChange={(value) => handleFieldChange('contact_email', value)}
                          onBlur={handleFieldBlur}
                          type="email"
                          confidenceClass={getConfidenceClassWithMissingCheck('contact_email')}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                      </div>
                    </div>
                    )}
                    
                    {/* Referral Information Section */}
                    {!triageNotesExpanded && (
                    <div className="referral-details">
                      <h4>Referral Information</h4>
                      <div className="detail-grid">
                        {/* AXIS213: Referral Type - aligned with Indici options (16 values) */}
                        <EditableField
                          label="Referral Type"
                          value={getFieldValue('referral_type')}
                          fieldName="referral_type"
                          className={isBlockedField('referral_type') ? 'blocked-field' : ''}
                          isEditing={editingField === 'referral_type'}
                          onStartEdit={() => handleStartEditing('referral_type')}
                          onChange={(value) => handleFieldChange('referral_type', value)}
                          onBlur={handleFieldBlur}
                          type="select"
                          options={[
                            { value: 'acute_knee_clinic', label: 'Acute Knee Clinic' },
                            { value: 'acute_shoulder_clinic', label: 'Acute Shoulder Clinic' },
                            { value: 'acute_sports_concussion_clinic', label: 'Acute Sports Concussion Clinic' },
                            { value: 'concussion_clinic', label: 'Concussion Clinic' },
                            { value: 'nutrition', label: 'Nutrition' },
                            { value: 'orthopaedic_surgery', label: 'Orthopaedic Surgery' },
                            { value: 'performance_health', label: 'Performance Health' },
                            { value: 'physiotherapy', label: 'Physiotherapy' },
                            { value: 'physiotherapy_pelvic', label: 'Physiotherapy - Pelvic' },
                            { value: 'physiotherapy_specialist', label: 'Physiotherapy - Specialist' },
                            { value: 'physiotherapy_vestibular', label: 'Physiotherapy – Vestibular' },
                            { value: 'podiatry', label: 'Podiatry' },
                            { value: 'procedures', label: 'Procedures' },
                            { value: 'shoulder_pain_clinic', label: 'Shoulder Pain Clinic' },
                            { value: 'sports_exercise_medicine', label: 'Sport and Exercise Medicine' },
                            { value: 'vocational_rehabilitation', label: 'Vocational Rehabilitation' },
                          ]}
                          confidenceClass={getConfidenceClassWithMissingCheck('referral_type')}
                          formatValue={toCapitalCase}
                          warning={(() => {
                            const rt = getFieldValue('referral_type');
                            return rt && ['osteoarthritis_clinic', 'reds'].includes(rt)
                              ? 'Value not in current list — please update'
                              : null;
                          })()}
                        />
                        <EditableField
                          label="Funder"
                          value={getFieldValue('funder')}
                          fieldName="funder"
                          className={isBlockedField('funder') ? 'blocked-field' : ''}
                          isEditing={editingField === 'funder'}
                          onStartEdit={() => handleStartEditing('funder')}
                          onChange={(value) => handleFunderChange(value)}
                          onBlur={handleFieldBlur}
                          type="select"
                          options={getFunderOptions()}
                          confidenceClass={getConfidenceClassWithMissingCheck('funder')}
                        />
                        {isAccFunder(getFieldValue('funder')) && (
                          <>
                            <div className="detail-item">
                              <label>ACC Claim Number:</label>
                              {editingField === 'acc_claim_number' && editingReferral ? (
                                <input
                                  type="text"
                                  value={editingReferral.acc_claim_number || ''}
                                  onChange={(e) => handleFieldChange('acc_claim_number', e.target.value)}
                                  onBlur={handleFieldBlur}
                                  className="form-control"
                                  autoFocus
                                />
                              ) : (
                                <div className="field-with-copy">
                                  <span 
                                    className={`editable-field ${getConfidenceClassWithMissingCheck('acc_claim_number')}`}
                                    onClick={() => handleStartEditing('acc_claim_number')}
                                  >
                                    {getFieldValue('acc_claim_number') || 'N/A'}
                                  </span>
                                  <CopyButton value={getFieldValue('acc_claim_number')} fieldName="acc_claim_number" />
                                </div>
                              )}
                            </div>
                            {!isSr && (
                            <div className="detail-item">
                              <label>ACC Status:</label>
                              <div className="validation-status-container">
                                <div className="validation-status-wrapper">
                                  {(() => {
                                    const accStatus = getACCStatusDisplay(currentReferral);
                                    return (
                                    <span
                                      className={`validation-status ${accStatus.className}`}
                                      title={currentReferral.acc_validation_reason_message || "ACC cover status from ACC registry"}
                                    >
                                      {accStatus.text}
                                    </span>
                                    );
                                  })()}
                                </div>
                                <button
                                  className="btn-refresh-validation"
                                  onClick={handleValidateACC}
                                  disabled={validating || !currentReferral.acc_claim_number}
                                  title={currentReferral.acc_claim_number ? "Re-validate ACC number" : "ACC claim number required"}
                                >
                                  {validating ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <FeatherIcon
                                      icon="refresh-cw"
                                      size={16}
                                      className="refresh-icon"
                                    />
                                  )}
                                </button>
                                {validationError && (
                                  <span className="validation-error-message" title={validationError}>
                                    ⚠️
                                  </span>
                                )}
                              </div>
                            </div>
                            )}
                          </>
                        )}
                        <div className="detail-item">
                          <label>Date of Injury:</label>
                          {editingField === 'date_of_injury' && editingReferral ? (
                            <input
                              type="date"
                              value={editingReferral.date_of_injury ? editingReferral.date_of_injury.split('T')[0] : ''}
                              onChange={(e) => handleFieldChange('date_of_injury', e.target.value)}
                              onBlur={handleFieldBlur}
                              className="form-control"
                              autoFocus
                            />
                          ) : (
                            <div className="field-with-copy">
                              <span 
                                className={`editable-field ${getConfidenceClassWithMissingCheck('date_of_injury')}`}
                                onClick={() => handleStartEditing('date_of_injury')}
                              >
                                {getFieldValue('date_of_injury') ? formatDateAsDDMMYYYY(getFieldValue('date_of_injury')) : 'N/A'}
                              </span>
                              <CopyButton value={getFieldValue('date_of_injury') ? formatDateAsDDMMYYYY(getFieldValue('date_of_injury')) : null} fieldName="date_of_injury" />
                            </div>
                          )}
                        </div>
                        <div className="detail-item">
                          <label>Mechanism of Injury:</label>
                          {editingField === 'mechanism_of_injury' && editingReferral ? (
                            <input
                              type="text"
                              value={editingReferral.mechanism_of_injury || ''}
                              onChange={(e) => handleFieldChange('mechanism_of_injury', e.target.value)}
                              onBlur={handleFieldBlur}
                              className="form-control"
                              placeholder="e.g., rugby tackle, fall from height, sports injury"
                              autoFocus
                            />
                          ) : (
                            <div className="field-with-copy">
                              <span 
                                className={`editable-field ${getConfidenceClassWithMissingCheck('mechanism_of_injury')}`}
                                onClick={() => handleStartEditing('mechanism_of_injury')}
                              >
                                {toCapitalCase(getFieldValue('mechanism_of_injury')) || 'N/A'}
                              </span>
                              <CopyButton value={getFieldValue('mechanism_of_injury')} fieldName="mechanism_of_injury" />
                            </div>
                          )}
                        </div>
                        {/* AXIS076: AE Validation Fields - Only for ACC Accredited Employer */}
                        {!isSr && (currentReferral?.funder === 'ACC – Accredited Employer' || 
                          currentReferral?.acc_validation_status_current === 'ACCREDITED_EMPLOYER') && (
                          <>
                            <EditableField
                              label="ACC Employer Name"
                              value={editingReferral?.acc_employer_name || ''}
                              fieldName="acc_employer_name"
                          className={isBlockedField('acc_employer_name') ? 'blocked-field' : ''}
                              isEditing={editingField === 'acc_employer_name'}
                              onStartEdit={() => handleStartEditing('acc_employer_name')}
                              onChange={(value) => handleFieldChange('acc_employer_name', value)}
                              onBlur={handleFieldBlur}
                              type="text"
                              placeholder="Enter ACC employer name"
                            />
                            
                            {currentReferral.ae_queue_entered_at && (
                              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                  <FeatherIcon icon="clock" size={16} />
                                  <strong>In AE Validation Queue</strong>
                                </div>
                                <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                                  Days in queue: {currentReferral.days_in_ae_queue || 0}
                                </p>
                                <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                                  ACC Info Received: {currentReferral.acc_info_received ? 'Yes' : 'No'}
                                </p>
                                {!currentReferral.acc_info_received && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleMarkAccInfoReceived}
                                    disabled={aeWorkflowLoading}
                                    isLoading={aeWorkflowLoading}
                                  >
                                    Mark ACC Info Received
                                  </Button>
                                )}
                                {currentReferral.acc_info_received && !currentReferral.ae_billing_completed && (
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleMarkAeBillingComplete}
                                    disabled={aeWorkflowLoading}
                                    isLoading={aeWorkflowLoading}
                                  >
                                    Mark AE Billing Complete
                                  </Button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        <div className="detail-item">
                          <label>Body Side:</label>
                          {editingField === 'body_side' && editingReferral ? (
                            <select
                              value={editingReferral.body_side || ''}
                              onChange={(e) => handleFieldChange('body_side', e.target.value)}
                              onBlur={handleFieldBlur}
                              className="form-control"
                              autoFocus
                            >
                              <option value="">Select...</option>
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                              <option value="bilateral">Bilateral</option>
                              <option value="not_applicable">N/A</option>
                            </select>
                          ) : (
                            <span 
                              className={`editable-field ${getConfidenceClassWithMissingCheck('body_side')}`}
                              onClick={() => handleStartEditing('body_side')}
                            >
                              {(() => {
                                const side = getFieldValue('body_side');
                                if (!side) return 'N/A';
                                if (side === 'not_applicable') return 'N/A';
                                return toCapitalCase(side);
                              })()}
                            </span>
                          )}
                        </div>
                        <div className="detail-item">
                          <label>Body Part:</label>
                          {editingField === 'body_part' && editingReferral ? (
                            <input
                              type="text"
                              value={editingReferral.body_part || ''}
                              onChange={(e) => handleFieldChange('body_part', e.target.value)}
                              onBlur={handleFieldBlur}
                              className="form-control"
                              autoFocus
                            />
                          ) : (
                            <span 
                              className={`editable-field ${getConfidenceClassWithMissingCheck('body_part')}`}
                              onClick={() => handleStartEditing('body_part')}
                            >
                              {toCapitalCase(getFieldValue('body_part')) || 'N/A'}
                            </span>
                          )}
                        </div>
                        <div className="detail-item">
                          <label>Imaging Performed:</label>
                          <div className="field-with-copy">
                            <span className={getConfidenceClassWithMissingCheck('referral_imaging_facility')}>{toCapitalCase(getFieldValue('imaging_facility') || currentReferral.imaging_facility) || 'N/A'}</span>
                            <CopyButton value={getFieldValue('imaging_facility') || currentReferral.imaging_facility} fieldName="imaging_facility" />
                          </div>
                        </div>
                        <EditableField
                          label="Referrer Name"
                          value={getFieldValue('referring_provider_name')}
                          fieldName="referring_provider_name"
                          className={isBlockedField('referring_provider_name') ? 'blocked-field' : ''}
                          isEditing={editingField === 'referring_provider_name'}
                          onStartEdit={() => handleStartEditing('referring_provider_name')}
                          onChange={(value) => handleFieldChange('referring_provider_name', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('referring_provider_name')}
                          formatValue={toCapitalCase}
                          showCopyButton
                          onCopy={handleCopy}
                          maxLength={255}
                        />
                        <EditableField
                          label="Referrer Organisation"
                          value={getFieldValue('referring_provider_org')}
                          fieldName="referring_provider_org"
                          className={isBlockedField('referring_provider_org') ? 'blocked-field' : ''}
                          isEditing={editingField === 'referring_provider_org'}
                          onStartEdit={() => handleStartEditing('referring_provider_org')}
                          onChange={(value) => handleFieldChange('referring_provider_org', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('referring_provider_org')}
                          formatValue={toCapitalCase}
                          showCopyButton
                          onCopy={handleCopy}
                          maxLength={255}
                        />
                        <EditableField
                          label="Referrer Email"
                          value={getFieldValue('referring_provider_email')}
                          fieldName="referring_provider_email"
                          className={isBlockedField('referring_provider_email') ? 'blocked-field' : ''}
                          isEditing={editingField === 'referring_provider_email'}
                          onStartEdit={() => handleStartEditing('referring_provider_email')}
                          onChange={(value) => handleFieldChange('referring_provider_email', value)}
                          onBlur={handleFieldBlur}
                          type="email"
                          confidenceClass={getConfidenceClassWithMissingCheck('referring_provider_email')}
                          showCopyButton
                          onCopy={handleCopy}
                        />
                        <EditableField
                          label="Referrer EDI"
                          value={getFieldValue('referring_provider_edi')}
                          fieldName="referring_provider_edi"
                          className={isBlockedField('referring_provider_edi') ? 'blocked-field' : ''}
                          isEditing={editingField === 'referring_provider_edi'}
                          onStartEdit={() => handleStartEditing('referring_provider_edi')}
                          onChange={(value) => handleFieldChange('referring_provider_edi', value)}
                          onBlur={handleFieldBlur}
                          type="text"
                          confidenceClass={getConfidenceClassWithMissingCheck('referring_provider_edi')}
                          formatValue={toCapitalCase}
                          showCopyButton
                          onCopy={handleCopy}
                          placeholder="EDI identifier"
                        />
                        <div className="detail-item">
                          <label>Received At:</label>
                          <div className="field-with-copy">
                            <span style={{ paddingLeft: '8px' }}>{currentReferral.received_at ? formatDateAsDDMMYYYY(currentReferral.received_at) : 'N/A'}</span>
                            <CopyButton value={currentReferral.received_at ? formatDateAsDDMMYYYY(currentReferral.received_at) : null} fieldName="received_at" />
                          </div>
                        </div>
                        <div className="detail-item">
                          <label>Attachment Count:</label>
                          <span style={{ paddingLeft: '8px' }}>{currentReferral.attachment_count || 0}</span>
                        </div>
                      </div>
                    </div>
                    )}
                    
                    {/* AXIS162: Triage Notes Section - Replaces Clinical Notes */}
                    <div className={`triage-notes-section ${triageNotesExpanded ? 'expanded' : ''}`}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0 }}>Triage Notes</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {/* Copy button for triage notes - only when triage data exists */}
                          {currentReferral.triage_data && editableTriageNotes && (
                            <button
                              className={`copy-btn ${triageNotesCopied ? 'copied' : ''}`}
                              onClick={copyTriageNotesToClipboard}
                              title={triageNotesCopied ? 'Copied to clipboard' : 'Copy to clipboard'}
                              aria-label={triageNotesCopied ? 'Copied to clipboard' : 'Copy to clipboard'}
                            >
                              {triageNotesCopied ? (
                                <FeatherIcon icon="check" size={18} />
                              ) : (
                                <FeatherIcon icon="copy" size={18} />
                              )}
                            </button>
                          )}
                          <Button
                            variant="icon"
                            onClick={() => setTriageNotesExpanded(!triageNotesExpanded)}
                            title={triageNotesExpanded ? 'Collapse' : 'Expand'}
                          >
                            <FeatherIcon icon={triageNotesExpanded ? "minimize-2" : "maximize-2"} size={18} />
                          </Button>
                        </div>
                      </div>

                      {/* Empty state when no triage data */}
                      {!currentReferral.triage_data ? (
                        <div className="triage-notes-empty-state">
                          <p className="triage-notes-empty-text">Triage not yet completed.</p>
                        </div>
                      ) : (
                        <>
                          {/* Structured Header: Triage Category and Complexity */}
                          <div className="triage-notes-header">
                            <div className="triage-header-item">
                              <label>Priority:</label>
                              <TriageCategoryBadge
                                category={currentReferral.triage_category_final}
                                variant="long"
                              />
                            </div>
                            <div className="triage-header-item">
                              <label>Complexity:</label>
                              <span className={`triage-badge ${currentReferral.triage_data.complexity || ''}`}>
                                {currentReferral.triage_data.complexity
                                  ? currentReferral.triage_data.complexity.charAt(0).toUpperCase() + currentReferral.triage_data.complexity.slice(1)
                                  : 'N/A'}
                              </span>
                            </div>
                          {/* Clinical Comments */}
                          {currentReferral.referral_comments && currentReferral.referral_comments.length > 0 && (
                            <div className="triage-field-group triage-comments-section">
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                                <span>Clinical Comments:</span>
                                <button
                                  className={`copy-btn ${commentsCopied ? 'copied' : ''}`}
                                  onClick={copyCommentsToClipboard}
                                  title={commentsCopied ? 'Copied to clipboard' : 'Copy comments to clipboard'}
                                  aria-label={commentsCopied ? 'Copied to clipboard' : 'Copy comments to clipboard'}
                                >
                                  {commentsCopied ? (
                                    <FeatherIcon icon="check" size={16} />
                                  ) : (
                                    <FeatherIcon icon="copy" size={16} />
                                  )}
                                </button>
                              </label>
                              <div className="triage-comments-list">
                                {currentReferral.referral_comments.map((comment, idx) => (
                                  <div key={idx} className="triage-comment-item">
                                    <div className="triage-comment-body">{comment.body}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          </div>


                          {/* Copyable Summary Block with Editable Fields */}
                          <div className="triage-notes-copyable-block">
                            {/* Key Phrases */}
                            <div className="triage-field-group">
                              <label>
                                Key Phrases:
                              </label>
                              {canEditTriageNotesFields ? (
                                <textarea
                                  value={(editableTriageNotes?.key_phrases_matched || []).join('\n')}
                                  onChange={(e) => updateEditableTriageField('key_phrases_matched', parseArrayField(e.target.value))}
                                  className="form-control triage-notes-textarea"
                                  rows={3}
                                  placeholder="Enter key phrases (one per line)..."
                                  disabled={!canEditTriageNotesFields}
                                />
                              ) : (
                                <div className="triage-field-display">
                                  {(editableTriageNotes?.key_phrases_matched || []).length > 0 ? (
                                    <ul className="triage-list">
                                      {(editableTriageNotes?.key_phrases_matched || []).map((phrase, idx) => (
                                        <li key={idx}>{phrase}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span className="triage-not-set">N/A</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Primary Symptoms */}
                            <div className="triage-field-group">
                              <label>
                                Primary Symptoms:
                              </label>
                              {canEditTriageNotesFields ? (
                                <textarea
                                  value={(editableTriageNotes?.primary_symptoms || []).join('\n')}
                                  onChange={(e) => updateEditableTriageField('primary_symptoms', parseArrayField(e.target.value))}
                                  className="form-control triage-notes-textarea"
                                  rows={3}
                                  placeholder="Enter primary symptoms (one per line)..."
                                  disabled={!canEditTriageNotesFields}
                                />
                              ) : (
                                <div className="triage-field-display">
                                  {(editableTriageNotes?.primary_symptoms || []).length > 0 ? (
                                    <ul className="triage-list">
                                      {(editableTriageNotes?.primary_symptoms || []).map((symptom, idx) => (
                                        <li key={idx}>{symptom}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span className="triage-not-set">N/A</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Functional Limitations */}
                            <div className="triage-field-group">
                              <label>
                                Functional Limitations:
                              </label>
                              {canEditTriageNotesFields ? (
                                <textarea
                                  value={(editableTriageNotes?.functional_limitations || []).join('\n')}
                                  onChange={(e) => updateEditableTriageField('functional_limitations', parseArrayField(e.target.value))}
                                  className="form-control triage-notes-textarea"
                                  rows={3}
                                  placeholder="Enter functional limitations (one per line)..."
                                  disabled={!canEditTriageNotesFields}
                                />
                              ) : (
                                <div className="triage-field-display">
                                  {(editableTriageNotes?.functional_limitations || []).length > 0 ? (
                                    <ul className="triage-list">
                                      {(editableTriageNotes?.functional_limitations || []).map((limitation, idx) => (
                                        <li key={idx}>{limitation}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span className="triage-not-set">N/A</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Provisional Diagnosis */}
                            <div className="triage-field-group">
                              <label>
                                Provisional Diagnosis:
                              </label>
                              {canEditTriageNotesFields ? (
                                <input
                                  type="text"
                                  value={editableTriageNotes?.provisional_diagnosis || ''}
                                  onChange={(e) => updateEditableTriageField('provisional_diagnosis', e.target.value || null)}
                                  className="form-control"
                                  placeholder="Enter provisional diagnosis..."
                                  disabled={!canEditTriageNotesFields}
                                />
                              ) : (
                                <div className="triage-field-display">
                                  <span>{editableTriageNotes?.provisional_diagnosis || 'N/A'}</span>
                                </div>
                              )}
                            </div>

                            {/* Athlete Status */}
                            <div className="triage-field-group">
                              <label>
                                Athlete Status:
                              </label>
                              <div className="triage-field-display">
                                <span>
                                  {editableTriageNotes?.athlete_status === true
                                    ? (currentReferral.triage_data.athlete_level && currentReferral.triage_data.sport_type
                                        ? `${currentReferral.triage_data.athlete_level} / ${currentReferral.triage_data.sport_type}`
                                        : 'Yes')
                                    : editableTriageNotes?.athlete_status === false
                                      ? 'No'
                                      : 'N/A'}
                                </span>
                              </div>
                            </div>

                            {/* Post Surgical */}
                            <div className="triage-field-group">
                              <label>
                                Post Surgical:
                              </label>
                              <div className="triage-field-display">
                                <span>
                                  {editableTriageNotes?.post_surgical_status === true
                                    ? 'Yes'
                                    : editableTriageNotes?.post_surgical_status === false
                                      ? 'No'
                                      : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                      </>

                  {(currentReferral.patient_suburb || currentReferral.practice_suburb || currentReferral.recommended_clinic_name || currentReferral.recommended_clinic_id) && (() => {
                    const confidence = currentReferral.clinic_recommendation_confidence;
                    const isPracticeFallback = currentReferral.patient_location_source === 'practice_address';
                    const hasClinic = !!(currentReferral.recommended_clinic_name || currentReferral.recommended_clinic_id);
                    return (
                      <div className="enrichment-card referral-location-section">
                        <h4>Location &amp; Clinic Routing</h4>
                        {isPracticeFallback && (
                          <div className="enrichment-banner-warning">
                            Patient address not found — location based on referring practice address.
                          </div>
                        )}
                        {confidence != null && confidence < 40 && (
                          <div className="enrichment-banner-alert">
                            Low confidence — manual clinic assignment recommended.
                          </div>
                        )}
                        <dl className="enrichment-grid">
                          {currentReferral.patient_suburb && (
                            <div>
                              <dt>Patient location</dt>
                              <dd>
                                {currentReferral.patient_suburb}
                                {currentReferral.patient_city ? `, ${currentReferral.patient_city}` : ''}
                              </dd>
                            </div>
                          )}
                          {currentReferral.practice_suburb && (
                            <div>
                              <dt>
                                Referrer location {!isPracticeFallback && <span style={{ opacity: 0.6 }}>(extracted)</span>}
                              </dt>
                              <dd>
                                {currentReferral.practice_suburb}
                                {currentReferral.practice_city ? `, ${currentReferral.practice_city}` : ''}
                              </dd>
                            </div>
                          )}
                          {currentReferral.patient_location_source && (
                            <div>
                              <dt>Source used</dt>
                              <dd>{isPracticeFallback ? 'Practice address' : 'Patient address'}</dd>
                            </div>
                          )}
                          {hasClinic && (
                            <div>
                              <dt>Nearest clinic</dt>
                              <dd>{currentReferral.recommended_clinic_name || `Clinic #${currentReferral.recommended_clinic_id}`}</dd>
                            </div>
                          )}
                          {currentReferral.clinic_recommendation_distance_km != null && (
                            <div>
                              <dt>Distance</dt>
                              <dd>{currentReferral.clinic_recommendation_distance_km.toFixed(1)} km</dd>
                            </div>
                          )}
                          {confidence != null && (
                            <div>
                              <dt>Confidence</dt>
                              <dd>{confidence}%</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    );
                  })()}

                  {(currentReferral.referrer_type_status || currentReferral.referring_provider_org) && (() => {
                    const rtConfidence = currentReferral.referrer_type_confidence;
                    const methodLabels: Record<string, string> = {
                      EXACT: 'Exact',
                      KEYWORD: 'Keyword',
                      SQL_FUZZY: 'Fuzzy',
                      AI: 'AI',
                      NONE: 'None',
                    };
                    return (
                      <div className="enrichment-card referral-referrer-type-section">
                        <h4>
                          Referrer Type
                          {currentReferral.referrer_type_overridden && (
                            <span className="edited-badge" style={{ marginLeft: '0.5rem' }}>Overridden</span>
                          )}
                        </h4>
                        {rtConfidence != null && rtConfidence < 40 && (
                          <div className="enrichment-banner-alert">
                            Low confidence — verify referrer type manually.
                          </div>
                        )}
                        <dl className="enrichment-grid">
                          <div>
                            <dt>Source text</dt>
                            <dd>{currentReferral.referring_provider_org || 'Not present'}</dd>
                          </div>
                          <div>
                            <dt>Resolved type</dt>
                            <dd>
                              {currentReferral.referrer_type_display_name
                                || currentReferral.referrer_type
                                || 'Not resolved'}
                            </dd>
                          </div>
                          {currentReferral.referrer_type_match_method && (
                            <div>
                              <dt>Match method</dt>
                              <dd>{methodLabels[currentReferral.referrer_type_match_method] || currentReferral.referrer_type_match_method}</dd>
                            </div>
                          )}
                          {rtConfidence != null && (
                            <div>
                              <dt>Confidence</dt>
                              <dd>{rtConfidence}%</dd>
                            </div>
                          )}
                          {currentReferral.referrer_type_status && (
                            <div>
                              <dt>Status</dt>
                              <dd>{currentReferral.referrer_type_status}</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    );
                  })()}

                  </div>

                  <div className="modal-pdf-preview-section">
                    {currentReferral.attachments && currentReferral.attachments.length > 0 ? (
                      <PDFPreviewPanel
                        key={currentReferral.attachments.map(a => a.id).join('-')}
                        attachments={(currentReferral.attachments || []) as any}
                        referralId={currentReferral.id}
                        isLoading={false}
                        userRole={userRole}
                        isSrReferral={isSr}
                        focusAttachmentId={focusAttachmentId}
                        onAttachmentUploaded={handleAttachmentUploaded}
                      />
                    ) : (
                      <div className="no-attachments-notice">
                        <p>No attachments yet</p>
                        <p className="notice-detail">
                          {isSr
                            ? 'Referral PDF will appear after ingest'
                            : 'Email body will appear here once received'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'attachments' && (() => {
                // Filter to only PDF attachments (for PDF viewer compatibility)
                // Also exclude inline images (email signatures, logos, CID-referenced images)
                const userAttachments = (currentReferral.attachments || []).filter(att => {
                  const filename = att.filename?.toLowerCase() || '';
                  if (filename.startsWith('inline_')) {
                    return false;
                  }
                  // Only show PDFs in the attachments list
                  const isPdfByType = att.type && att.type.toLowerCase() === 'pdf';
                  const isPdfByMimeType = att.mime_type && att.mime_type.toLowerCase() === 'application/pdf';
                  const isPdfByFilename = filename.endsWith('.pdf');
                  return isPdfByType || isPdfByMimeType || isPdfByFilename;
                });

                // AXIS212: Check if user can upload attachments (email referrals only)
                const canUploadAttachment = !isSr && ['admin', 'manager', 'clinician'].includes(userRole);

                return (
                  <div className="attachments-tab">
                    <div className="attachments-header">
                      <h4>Attachments ({userAttachments.length})</h4>
                      {/* AXIS212: Upload Attachment button - visible only to allowed roles */}
                      {canUploadAttachment && (
                        <>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept=".pdf,.docx,.doc,.jpeg,.jpg,.png"
                            style={{ display: 'none' }}
                            disabled={uploadingAttachment}
                          />
                          <Button
                            variant="primary"
                            onClick={handleUploadClick}
                            disabled={uploadingAttachment}
                            isLoading={uploadingAttachment}
                          >
                            {uploadingAttachment ? `Uploading ${uploadProgress}%` : 'Upload Attachment'}
                          </Button>
                        </>
                      )}
                    </div>

                    {/* AXIS212: Upload error message */}
                    {uploadError && (
                      <div className="upload-error-banner" style={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        padding: '0.75rem 1rem',
                        borderRadius: '4px',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FeatherIcon icon="alert-circle" size={16} />
                        <span>{uploadError}</span>
                        <button
                          onClick={() => setUploadError(null)}
                          style={{
                            marginLeft: 'auto',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#c62828'
                          }}
                          title="Dismiss"
                        >
                          <FeatherIcon icon="x" size={14} />
                        </button>
                      </div>
                    )}

                    {userAttachments.length === 0 ? (
                      <div className="empty-state">
                        <p>No attachments available for this currentReferral.</p>
                      </div>
                    ) : (
                      <div className="attachments-list">
                        {userAttachments.map(attachment => (
                          <div key={attachment.id} className="attachment-item">
                            <div className="attachment-info">
                              <div className="attachment-name">{attachment.filename}</div>
                              <div className="attachment-meta">
                                {formatFileSize(attachment.size)} • {attachment.type.toUpperCase()} • 
                                {attachment.uploadedAt ? new Date(attachment.uploadedAt).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            <div className="attachment-actions">
                              <Button 
                                size="sm"
                                variant="primary"
                                onClick={() => handleDownloadAttachment(attachment)}
                                disabled={downloadingAttachments.has(attachment.id)}
                                isLoading={downloadingAttachments.has(attachment.id)}
                              >
                                Download
                              </Button>
                              {!isSr && (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDeleteClick(attachment)}
                                disabled={deletingAttachments.has(attachment.id)}
                                isLoading={deletingAttachments.has(attachment.id)}
                                title="Remove this attachment"
                              >
                                Remove
                              </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <IngestionWarningsBanner
                      warnings={currentReferral.ingestion_warnings || []}
                      dismissed={dismissedIngestionWarnings}
                      onDismiss={() => setDismissedIngestionWarnings(true)}
                    />
                  </div>
                );
              })()}

            </div>
          </div>
        </div>
      </div>

      {/* Mark Complete Confirmation Dialog */}
      {currentReferral && isSr ? (
        !isSrCancelled ? (
          <SRCompleteResponseModal
            referral={currentReferral}
            isOpen={showCompleteConfirm}
            onClose={cancelMarkComplete}
            onConfirm={(payload) => confirmMarkComplete(payload)}
            isLoading={completing}
            error={completionError}
          />
        ) : null
      ) : (
      <ConfirmationModal
        isOpen={showCompleteConfirm}
        title="Mark Referral as Complete"
        message={missingFields.length > 0
          ? "This referral cannot be marked as complete because it is missing required information."
          : getMarkCompleteConfirmCopy(
              canCompleteMiscIcpWithoutIndici(currentReferral),
            ).message}
        confirmLabel={missingFields.length > 0
          ? "Mark Complete"
          : getMarkCompleteConfirmCopy(
              canCompleteMiscIcpWithoutIndici(currentReferral),
            ).confirmLabel}
        onConfirm={() => confirmMarkComplete()}
        onCancel={cancelMarkComplete}
        isLoading={completing}
        variant="success"
        details={[
          { label: 'Patient', value: `${currentReferral.patient_first_name} ${currentReferral.patient_last_name}` }
        ]}
        error={completionError}
        additionalContent={missingFields.length > 0 ? (
          <div style={{ marginTop: '16px' }}>
            <p style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>
              Missing Required Fields:
            </p>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px',
              color: '#d32f2f',
              listStyle: 'disc'
            }}>
              {missingFields.map((field, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>
                  {getFieldDisplayLabel(field)}
                </li>
              ))}
            </ul>
            <p style={{ 
              marginTop: '12px', 
              fontSize: '0.9em', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Please edit the referral to populate these fields before marking it as complete.
            </p>
          </div>
        ) : undefined}
        modalClassName="complete-confirm-modal"
        preventCloseOnOverlayClick={completing}
      />
      )}

      {/* Resolve Escalation Confirmation Dialog */}
      <ConfirmationModal
        isOpen={showResolveConfirm}
        title="Resolve Escalation"
        message="Mark this escalation as resolved?"
        confirmLabel="Resolve"
        onConfirm={confirmResolveEscalation}
        onCancel={cancelResolveEscalation}
        isLoading={resolvingEscalation}
        variant="primary"
        details={[
          { label: 'Patient', value: `${currentReferral.patient_first_name} ${currentReferral.patient_last_name}` }
        ]}
        error={escalationError}
      />

      {/* Close Confirmation Dialog */}
      <ConfirmationModal
        isOpen={showCloseConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close and discard these changes?"
        confirmLabel="Discard Changes"
        cancelLabel="Keep Editing"
        onConfirm={confirmClose}
        onCancel={cancelClose}
        variant="warning"
        details={[
          { label: 'Patient', value: `${currentReferral.patient_first_name} ${currentReferral.patient_last_name}` },
          { label: 'Referral ID', value: currentReferral.id }
        ]}
        modalClassName="close-confirm-modal"
      />

      {/* Escalation Modal */}
      {showEscalationModal && (
        <div className="modal-overlay" onClick={() => setShowEscalationModal(false)}>
          <div className="modal-content escalation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Escalate to Admin</h3>
              <Button
                variant="icon"
                onClick={() => {
                  setShowEscalationModal(false);
                  setEscalationNote('');
                  setEscalationError(null);
                }}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
            <div className="modal-body">
              <p>Please provide a note explaining why this referral needs to be escalated to an admin:</p>
              <textarea
                value={escalationNote}
                onChange={(e) => setEscalationNote(e.target.value)}
                className="form-control"
                rows={4}
                placeholder="Enter escalation reason..."
                autoFocus
              />
              {escalationError && (
                <div className="error-message" style={{ marginTop: '10px', color: '#d32f2f' }}>
                  {escalationError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEscalationModal(false);
                  setEscalationNote('');
                  setEscalationError(null);
                }}
                disabled={escalatingReferral}
              >
                Cancel
              </Button>
              <Button
                variant="warning"
                onClick={handleEscalate}
                disabled={escalatingReferral || !escalationNote.trim()}
                isLoading={escalatingReferral}
              >
                Escalate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AXIS210: Approve for Triage Modal */}
      {showApproveForTriageModal && (
        <div className="modal-overlay" onClick={() => setShowApproveForTriageModal(false)}>
          <div className="modal-content approve-for-triage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve for Triage</h3>
              <Button
                variant="icon"
                onClick={() => {
                  setShowApproveForTriageModal(false);
                  setApproveForTriageNote('');
                  setApproveForTriageError(null);
                }}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
            <div className="modal-body">
              <p>
                This will promote the referral from Miscellaneous to the AI Triaging pipeline.
                The referral will be sent for clinical triage review.
              </p>
              <p>Please provide an optional note explaining why this referral is being promoted:</p>
              <textarea
                value={approveForTriageNote}
                onChange={(e) => setApproveForTriageNote(e.target.value)}
                className="form-control"
                rows={4}
                placeholder="Enter reason for promoting to triage (optional)..."
                autoFocus
              />
              {approveForTriageError && (
                <div className="error-message" style={{ marginTop: '10px', color: '#d32f2f' }}>
                  {approveForTriageError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowApproveForTriageModal(false);
                  setApproveForTriageNote('');
                  setApproveForTriageError(null);
                }}
                disabled={approvingForTriage}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleApproveForTriage}
                disabled={approvingForTriage}
                isLoading={approvingForTriage}
              >
                Approve for Triage
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AXISREF482: Approve declined ACC claim Modal */}
      {showDeclinedAccApprovalModal && (
        <div className="modal-overlay" onClick={() => !approvingDeclinedAcc && setShowDeclinedAccApprovalModal(false)}>
          <div className="modal-content declined-acc-approval-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve declined ACC claim</h3>
              <Button
                variant="icon"
                onClick={() => {
                  setShowDeclinedAccApprovalModal(false);
                  setDeclinedAccApprovalError(null);
                }}
                disabled={approvingDeclinedAcc}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
            <div className="modal-body">
              <p>ACC declined this claim. Approve it only when staff have reviewed the case and are satisfied it may proceed.</p>
              <label htmlFor="declined-acc-approval-note">Approval note <span aria-hidden="true">*</span></label>
              <textarea
                id="declined-acc-approval-note"
                value={declinedAccApprovalNote}
                onChange={(e) => setDeclinedAccApprovalNote(e.target.value)}
                className="form-control"
                rows={5}
                maxLength={5000}
                placeholder="Explain why this declined claim is being approved..."
                autoFocus
                required
              />
              <p className="form-help">This note, the approving staff member, and the time will be retained in the referral audit history.</p>
              {declinedAccApprovalError && (
                <div className="error-message" role="alert">{declinedAccApprovalError}</div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeclinedAccApprovalModal(false);
                  setDeclinedAccApprovalError(null);
                }}
                disabled={approvingDeclinedAcc}
              >
                Cancel
              </Button>
              <Button
                variant="warning"
                onClick={handleApproveDeclinedAcc}
                disabled={approvingDeclinedAcc || !declinedAccApprovalNote.trim()}
                isLoading={approvingDeclinedAcc}
              >
                Approve claim
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Awaiting Reply Modal */}
      {showAwaitingReplyModal && (
        <div className="modal-overlay" onClick={() => setShowAwaitingReplyModal(false)}>
          <div className="modal-content awaiting-reply-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mark as Awaiting Reply</h3>
              <Button
                variant="icon"
                onClick={() => {
                  setShowAwaitingReplyModal(false);
                  setAwaitingReplyNote('');
                  setAwaitingReplyError(null);
                }}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
            <div className="modal-body">
              <p>Please provide a note explaining what information you are waiting for:</p>
              <textarea
                value={awaitingReplyNote}
                onChange={(e) => setAwaitingReplyNote(e.target.value)}
                className="form-control"
                rows={4}
                placeholder="Enter note about what you're awaiting..."
                autoFocus
              />
              {awaitingReplyError && (
                <div className="error-message" style={{ marginTop: '10px', color: '#d32f2f' }}>
                  {awaitingReplyError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAwaitingReplyModal(false);
                  setAwaitingReplyNote('');
                  setAwaitingReplyError(null);
                }}
                disabled={markingAwaitingReply}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleMarkAwaitingReply}
                disabled={markingAwaitingReply || !awaitingReplyNote.trim()}
                isLoading={markingAwaitingReply}
              >
                Mark as Awaiting Reply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Composer */}
      {showFollowUpComposer && currentReferral && !isSr && (
        <FollowUpComposer
          referral={currentReferral}
          isOpen={showFollowUpComposer}
          onClose={() => setShowFollowUpComposer(false)}
        />
      )}

      {showSRMessageComposer && currentReferral && isSr && !isSrCancelled && (
        <SRMessageComposer
          referral={currentReferral}
          isOpen={showSRMessageComposer}
          onClose={() => setShowSRMessageComposer(false)}
        />
      )}

      {showSRClarificationModal && currentReferral && isSr && !isSrCancelled && (
        <SRClarificationModal
          referral={currentReferral}
          isOpen={showSRClarificationModal}
          onClose={() => setShowSRClarificationModal(false)}
          onSent={() => {
            setShowSRClarificationModal(false);
            onClose();
          }}
          loadMissingFieldsDraft={(referralId) =>
            ApiService.getSRClarificationDraft(referralId).then((result) => result.body)
          }
          onSend={async (referralId, body, awaitingReplyNote) => {
            await ApiService.requestSRClarification(referralId, body, {
              awaitingReplyNote,
            });
          }}
        />
      )}

      {/* AXIS212: Delete Attachment Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Remove Attachment"
        message="Are you sure you want to remove this attachment? This action cannot be undone."
        confirmLabel="Remove"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={attachmentToDelete ? deletingAttachments.has(attachmentToDelete.id) : false}
        variant="danger"
        details={attachmentToDelete ? [
          { label: 'Filename', value: attachmentToDelete.filename },
          { label: 'Size', value: formatFileSize(attachmentToDelete.size) }
        ] : []}
      />

      {/* AXIS235: AE Workflow Modals */}
      
      {/* AXIS235: "Do you have the Accredited Employer Name?" prompt */}
      <ConfirmationModal
        isOpen={showAeNamePrompt}
        title="Accredited Employer Name"
        message="Do you have the Accredited Employer Name?"
        details={[
          { label: 'Patient', value: `${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}` },
          { label: 'ACC Claim Number', value: currentReferral?.acc_claim_number || '' }
        ]}
        confirmLabel="Yes - Mark Complete"
        cancelLabel="No - Send ACC Email"
        onConfirm={handleAeNameYes}
        onCancel={handleAeNameNo}
        variant="primary"
      />

      <BillToCompletedModal
        isOpen={showBillToCompleted}
        patientName={`${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}`}
        accClaimNumber={currentReferral?.acc_claim_number || ''}
        onYes={handleBillToCompletedYes}
        onNo={handleBillToCompletedNo}
        onCancel={() => setShowBillToCompleted(false)}
        isLoading={aeWorkflowLoading}
      />

      <AlertCompletedModal
        isOpen={showAlertCompleted}
        patientName={`${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}`}
        onConfirm={handleAlertCompletedConfirm}
        onCancel={() => setShowAlertCompleted(false)}
        isLoading={aeWorkflowLoading}
      />

      <SendAccEmailModal
        isOpen={showSendAccEmail}
        patientName={`${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}`}
        accClaimNumber={currentReferral?.acc_claim_number || ''}
        onConfirm={handleSendAccEmailConfirm}
        onCancel={() => setShowSendAccEmail(false)}
        isLoading={aeWorkflowLoading}
      />

      {/* AXIS235: Send ACC Email - Final confirmation */}
      <ConfirmationModal
        isOpen={showSendAccEmailConfirm}
        title="Mark Referral as Awaiting AE Validation"
        message="Are you sure you want to mark this referral as completed? This indicates it has been entered into Indici and that ACC has been emailed for the Accredited Employer Name."
        details={[
          { label: 'Patient', value: `${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}` },
          { label: 'ACC Claim Number', value: currentReferral?.acc_claim_number || '' }
        ]}
        confirmLabel="Confirm - Mark as Awaiting AE Validation"
        onConfirm={handleConfirmSendAccEmail}
        onCancel={() => setShowSendAccEmailConfirm(false)}
        isLoading={aeWorkflowLoading}
        variant="success"
      />

      {/* AXIS235: Mark AE Billing Complete confirmation */}
      <ConfirmationModal
        isOpen={showMarkAeBillingComplete}
        title="Mark AE Billing Complete"
        message="Are you sure you want to mark AE billing as complete? This will move the referral to the Complete queue."
        details={[
          { label: 'Patient', value: `${currentReferral?.patient_first_name || ''} ${currentReferral?.patient_last_name || ''}` },
          { label: 'Referral ID', value: currentReferral?.id || '' }
        ]}
        confirmLabel="Mark Complete"
        onConfirm={handleConfirmMarkAeBillingComplete}
        onCancel={() => setShowMarkAeBillingComplete(false)}
        isLoading={aeWorkflowLoading}
        variant="success"
      />

    </>
  );
};
